import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { subcontractAPI, inventoryAPI, suppliersAPI, contractsAPI } from '@/lib/api';
import { EntityLink } from '@/components/EntityLink';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@/components/ui/tabs';
import { Plus, ChevronLeft, ChevronRight, ArrowUpRight, ArrowDownLeft, Download, Trash2, Send, PackageCheck, Eye, Wand2, Loader2, Truck, FileCheck, X, Search, CheckCircle2, Mail, AlertTriangle, GitFork, RotateCcw, Upload, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { SearchableSelect } from '@/components/SearchableSelect';
import { ChallanTrackingDialog } from '@/components/ChallanTrackingDialog';
import { QtyDiscrepancyDialog } from '@/components/QtyDiscrepancyDialog';

const STATUS_COLORS = {
  draft: 'bg-slate-50 text-slate-600 border-slate-200',
  sent: 'bg-blue-50 text-blue-700 border-blue-200',
  partially_received: 'bg-amber-50 text-amber-700 border-amber-200',
  all_received: 'bg-teal-50 text-teal-700 border-teal-200',
  received: 'bg-teal-50 text-teal-700 border-teal-200',
  closed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export default function SubcontractPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [challans, setChallans] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [deleting, setDeleting] = useState(null);
  const [activeTab, setActiveTab] = useState('outbound');
  const limit = 25;

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [jobWorkItems, setJobWorkItems] = useState([]); // For inbound subcontracting
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [challanForm, setChallanForm] = useState({
    order_type: 'outbound', supplier_id: '', customer_id: '', purchase_order_number: '', remarks: '', customer_name: '', due_date: '',
    customer_challan_number: '', // Mandatory for inbound
    traceability_type: 'challan_sr', // 'challan_sr' | 'work_order' | 'auto'
    purchase_order_ids: [], // Array of PO IDs
    route_id: '', // BOM/route linkage for manufacturing outbound
    contract_po_id: '', // Links to contract PO for BOM validation
  });
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [challanItems, setChallanItems] = useState([{ item_id: '', quantity: '', process_description: '', expected_return_date: '', work_order_number: '', raw_material_batch_number: '' }]);
  const [expectedOutputs, setExpectedOutputs] = useState([{ item_id: '', quantity: '' }]);
  const [loadingRecalled, setLoadingRecalled] = useState(false);
  const [recalledCount, setRecalledCount] = useState(0);
  
  // PO selection for create dialog
  const [createPOSearch, setCreatePOSearch] = useState('');
  const [createAvailablePOs, setCreateAvailablePOs] = useState([]);
  const [loadingCreatePOs, setLoadingCreatePOs] = useState(false);

  // Inbound customer PO state
  const [inboundCustomerPOs, setInboundCustomerPOs] = useState({ contract_pos: [], historical_pos: [] });
  const [loadingInboundPOs, setLoadingInboundPOs] = useState(false);
  const [inboundPOSearch, setInboundPOSearch] = useState('');
  const [inboundPODropdownOpen, setInboundPODropdownOpen] = useState(false);

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailChallan, setDetailChallan] = useState(null);

  // Tracking dialog
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [trackingChallan, setTrackingChallan] = useState(null);

  // Receive dialog
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveChallan, setReceiveChallan] = useState(null);
  const [receiveItems, setReceiveItems] = useState([]);
  const [godowns, setGodowns] = useState([]);

  // All items for expected output selection
  const [finishedGoodItems, setFinishedGoodItems] = useState([]);
  const [dispatchStatusOpen, setDispatchStatusOpen] = useState(false);
  const [dispatchStatus, setDispatchStatus] = useState(null);

  // Short/Excess Qty Discrepancy dialog
  const [discrepancyOpen, setDiscrepancyOpen] = useState(false);
  const [discrepancyChallan, setDiscrepancyChallan] = useState(null);
  const [loadingDispatchStatus, setLoadingDispatchStatus] = useState(false);

  // PO Linking state
  const [linkPOOpen, setLinkPOOpen] = useState(false);
  const [linkPOChallan, setLinkPOChallan] = useState(null);
  const [availablePOs, setAvailablePOs] = useState([]);
  const [selectedPOs, setSelectedPOs] = useState([]);
  const [poSearchTerm, setPOSearchTerm] = useState('');
  const [linkingPO, setLinkingPO] = useState(false);
  const [loadingPOs, setLoadingPOs] = useState(false);
  // Inbound PO linking state
  const [linkInboundPOs, setLinkInboundPOs] = useState({ contract_pos: [], historical_pos: [] });
  const [linkInboundPOSearch, setLinkInboundPOSearch] = useState('');
  const [linkInboundSelectedPO, setLinkInboundSelectedPO] = useState('');
  const [linkInboundContractPOId, setLinkInboundContractPOId] = useState('');

  // Send with Email dialog state
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [sendEmailChallan, setSendEmailChallan] = useState(null);
  const [sendEmailForm, setSendEmailForm] = useState({ send_email: true, email_to: '', email_cc: '', email_message: '' });
  const [sendingWithEmail, setSendingWithEmail] = useState(false);
  const [loadingSupplierEmail, setLoadingSupplierEmail] = useState(false);
  const [receiptMode, setReceiptMode] = useState('same_item');

  // Service Entry & Closure state
  const [serviceEntryOpen, setServiceEntryOpen] = useState(false);
  const [serviceEntryChallan, setServiceEntryChallan] = useState(null);
  const [serviceEntries, setServiceEntries] = useState([]);
  const [serviceForm, setServiceForm] = useState({
    invoice_number: '', invoice_date: '', service_item_name: 'Job Work Charges',
    ok_quantity: '', rate_per_unit: '', total_amount: '', remarks: '',
  });
  const [submittingService, setSubmittingService] = useState(false);
  const [closingChallan, setClosingChallan] = useState(false); // same_item | processed | rejected_rm | return_as_is | scrap
  const [forceCloseRemarks, setForceCloseRemarks] = useState('');
  const [forceClosePrompt, setForceClosePrompt] = useState(null); // { challan, type: 'inbound'|'outbound' }
  const [reopenPrompt, setReopenPrompt] = useState(null); // { challan }
  const [reopenRemarks, setReopenRemarks] = useState('');
  const [reopening, setReopening] = useState(false);

  useEffect(() => { loadChallans(); }, [page, statusFilter, activeTab, searchTerm]);

  // Update receive items when receipt mode changes (for BOM-linked challans)
  useEffect(() => {
    if (!receiveChallan || !receiveOpen) return;
    const hasBom = !!(receiveChallan.route_id || receiveChallan.route_snapshot);
    if (!hasBom && receiptMode === 'same_item') return; // No change needed
    
    const items = (() => {
      if (receiptMode === 'same_item' || receiptMode === 'rejected_rm' || receiptMode === 'return_as_is') {
        return (receiveChallan.items || []).map((ci, idx) => ({
          item_id: ci.item_id, item_name: ci.item_name, line_index: idx,
          receipt_type: receiptMode, sent_qty: ci.quantity,
          already_received: ci.received_quantity || 0,
          remaining_qty: Math.max(0, (ci.quantity || 0) - (ci.received_quantity || 0)),
          received_quantity: '', ok_quantity: '', rejected_quantity: '0', rework_quantity: '0',
          godown_id: '', traceability_id_description: '', traceability_seed: '', output_quantity: 1, sr_number: idx + 1,
        }));
      } else {
        const outputs = receiveChallan.expected_output_items || [];
        const relevant = receiptMode === 'scrap' ? outputs.filter(o => o.output_type === 'scrap') : outputs.filter(o => o.output_type !== 'scrap');
        return relevant.map((eo, idx) => ({
          item_id: eo.item_id, item_name: eo.item_name || '', line_index: null,
          receipt_type: receiptMode, expected_qty: eo.quantity || 0,
          already_received: eo.received_quantity || 0,
          remaining_qty: Math.max(0, (eo.quantity || 0) - (eo.received_quantity || 0)),
          received_quantity: '', ok_quantity: '', rejected_quantity: '0', rework_quantity: '0',
          godown_id: '', traceability_id_description: '', traceability_seed: '', output_quantity: 1, sr_number: idx + 1,
        }));
      }
    })();
    setReceiveItems(items);
  }, [receiptMode]); // eslint-disable-line

  const loadChallans = async () => {
    setLoading(true);
    try {
      const params = { page, limit, order_type: activeTab };
      if (statusFilter === 'open') {
        params.status_in = activeTab === 'inbound' 
          ? 'draft,sent,partially_received,all_received,received' 
          : 'draft,sent,partially_received,all_received';
      } else if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      const res = await subcontractAPI.listChallans(params);
      setChallans(res.data.challans || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load challans'); }
    finally { setLoading(false); }
  };

  // PO Linking Functions
  const openLinkPO = async (challan) => {
    setLinkPOChallan(challan);
    setPOSearchTerm('');
    setLinkPOOpen(true);

    if (challan.order_type === 'inbound') {
      // Inbound: load customer POs (contract + historical)
      setLinkInboundSelectedPO(challan.purchase_order_number || '');
      setLinkInboundContractPOId(challan.contract_po_id || '');
      setLinkInboundPOSearch('');
      setLoadingPOs(true);
      try {
        const res = await subcontractAPI.getCustomerPOs(challan.supplier_id);
        setLinkInboundPOs(res.data || { contract_pos: [], historical_pos: [] });
      } catch { setLinkInboundPOs({ contract_pos: [], historical_pos: [] }); }
      finally { setLoadingPOs(false); }
    } else {
      // Outbound: load supplier POs from purchase_requests
      setSelectedPOs(challan.purchase_order_ids || []);
      loadAvailablePOs(challan.supplier_id);
    }
  };

  const loadAvailablePOs = async (supplierId, search = '') => {
    setLoadingPOs(true);
    try {
      const params = { limit: 50 };
      if (supplierId) params.supplier_id = supplierId;
      if (search) params.search = search;
      const res = await subcontractAPI.getPOsForLinking(params);
      setAvailablePOs(res.data.purchase_orders || []);
    } catch (err) {
      toast.error('Failed to load purchase orders');
    } finally {
      setLoadingPOs(false);
    }
  };

  const togglePOSelection = (poId) => {
    setSelectedPOs(prev => 
      prev.includes(poId) 
        ? prev.filter(id => id !== poId) 
        : [...prev, poId]
    );
  };

  const savePOLink = async () => {
    if (!linkPOChallan) return;
    setLinkingPO(true);
    try {
      if (linkPOChallan.order_type === 'inbound') {
        // Inbound: send PO number + optional contract_po_id
        await subcontractAPI.linkPO(linkPOChallan.id, {
          purchase_order_number: linkInboundSelectedPO,
          contract_po_id: linkInboundContractPOId,
        });
      } else {
        // Outbound: send PO IDs list
        const selectedPONumbers = availablePOs
          .filter(po => selectedPOs.includes(po.id))
          .map(po => po.po_number)
          .join(', ');
        await subcontractAPI.linkPO(linkPOChallan.id, {
          purchase_order_ids: selectedPOs,
          purchase_order_number: selectedPONumbers,
        });
      }
      toast.success('PO link updated successfully');
      setLinkPOOpen(false);
      loadChallans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update PO link');
    } finally {
      setLinkingPO(false);
    }
  };

  const openCreate = async (type) => {
    try {
      const promises = [
        inventoryAPI.listItems({ limit: 500, sort_by: 'item_name', sort_order: 'asc' }),
        suppliersAPI.approvedList(),
      ];
      if (type === 'inbound') {
        promises.push(contractsAPI.activeCustomers());
        promises.push(inventoryAPI.listJobWorkItems()); // Get Job Work Items for inbound
        promises.push(inventoryAPI.listItems({ limit: 500, item_type: 'Finished Good' })); // For expected outputs
      }
      const results = await Promise.all(promises);
      setAllItems(results[0].data.items || []);
      setSuppliers(results[1].data || []);
      if (type === 'inbound') {
        setCustomers(results[2]?.data || []);
        setJobWorkItems(results[3]?.data || []);
        setFinishedGoodItems(results[4]?.data?.items || results[0].data.items || []);
      }
      setChallanForm({ order_type: type, supplier_id: '', customer_id: '', purchase_order_number: '', remarks: '', customer_name: '', due_date: '', customer_challan_number: '', traceability_type: 'challan_sr', purchase_order_ids: [], route_id: '', contract_po_id: '' });
      setChallanItems([{ item_id: '', quantity: '', process_description: '', expected_return_date: '' }]);
      setExpectedOutputs([{ item_id: '', quantity: '' }]);
      setSelectedRoute(null);
      setCreateAvailablePOs([]);
      setCreatePOSearch('');
      setInboundCustomerPOs({ contract_pos: [], historical_pos: [] });
      setInboundPOSearch('');
      setInboundPODropdownOpen(false);
      setCreateOpen(true);
    } catch { toast.error('Failed to load data'); }
  };

  // Load POs when supplier changes in create dialog
  const loadPOsForSupplier = async (supplierId, search = '') => {
    if (!supplierId) {
      setCreateAvailablePOs([]);
      return;
    }
    setLoadingCreatePOs(true);
    try {
      const params = { supplier_id: supplierId, limit: 50 };
      if (search) params.search = search;
      const res = await subcontractAPI.getPOsForLinking(params);
      setCreateAvailablePOs(res.data.purchase_orders || []);
    } catch (err) {
      console.error('Failed to load POs', err);
    } finally {
      setLoadingCreatePOs(false);
    }
  };

  // Load customer POs for inbound challan (historical + contract POs)
  const loadInboundCustomerPOs = async (customerId) => {
    if (!customerId) {
      setInboundCustomerPOs({ contract_pos: [], historical_pos: [] });
      return;
    }
    setLoadingInboundPOs(true);
    try {
      const res = await subcontractAPI.getCustomerPOs(customerId);
      setInboundCustomerPOs(res.data || { contract_pos: [], historical_pos: [] });
    } catch (err) {
      console.error('Failed to load customer POs', err);
      setInboundCustomerPOs({ contract_pos: [], historical_pos: [] });
    } finally {
      setLoadingInboundPOs(false);
    }
  };

  const toggleCreatePOSelection = (poId) => {
    setChallanForm(prev => {
      const isInbound = prev.order_type === 'inbound';
      if (prev.purchase_order_ids.includes(poId)) {
        // Deselect
        return { ...prev, purchase_order_ids: prev.purchase_order_ids.filter(id => id !== poId) };
      }
      // For inbound: replace (only 1 PO). For outbound: add to list.
      return { ...prev, purchase_order_ids: isInbound ? [poId] : [...prev.purchase_order_ids, poId] };
    });
  };

  const addChallanItem = () => {
    setChallanItems(prev => [...prev, { item_id: '', quantity: '', process_description: '', expected_return_date: '', work_order_number: '', raw_material_batch_number: '' }]);
  };

  const removeChallanItem = (idx) => {
    if (challanItems.length <= 1) return;
    setChallanItems(prev => prev.filter((_, i) => i !== idx));
  };

  const updateChallanItem = (idx, field, value) => {
    setChallanItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  // ---- Inbound challan Excel import ----
  const importFileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [notifyingLowStock, setNotifyingLowStock] = useState(false);

  const handleNotifyLowStockCustomers = async () => {
    // Step 1: dry-run preview
    let preview;
    try {
      const res = await subcontractAPI.notifyLowStockCustomers({ dry_run: true });
      preview = res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to preview low-stock notifications');
      return;
    }
    const total = preview?.items_total ?? 0;
    const batches = preview?.batches?.length ?? 0;
    if (!total) {
      toast.success('No items below reorder level — nothing to notify');
      return;
    }
    if (!batches) {
      toast.error('Items found but no recipient could be resolved. Check program/PO/customer emails.');
      return;
    }
    const summary = (preview.batches || [])
      .map(b => {
        const cc = (b.cc_emails || []).length ? `\n   Cc: ${b.cc_emails.join(', ')}` : '';
        return `• ${b.customer_name}: ${b.items_count} item(s)\n   To: ${(b.to_emails || []).join(', ')}${cc}`;
      })
      .join('\n');
    if (!window.confirm(
      `Send low-stock email to ${batches} customer batch${batches === 1 ? '' : 'es'} (${total} item${total === 1 ? '' : 's'} total)?\n\n${summary}`
    )) return;

    setNotifyingLowStock(true);
    try {
      const res = await subcontractAPI.notifyLowStockCustomers();
      const sent = res.data?.sent ?? 0;
      const errs = (res.data?.errors || []).length;
      if (errs > 0) {
        toast.warning(`Sent ${sent} email(s); ${errs} failed (see console)`);
        console.error('Low-stock notification errors:', res.data.errors);
      } else {
        toast.success(`Sent ${sent} low-stock email${sent === 1 ? '' : 's'}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send notifications');
    } finally {
      setNotifyingLowStock(false);
    }
  };

  const handleDownloadImportTemplate = async () => {
    try {
      const res = await subcontractAPI.downloadImportTemplate();
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Inbound_Challan_Import_Template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download template');
    }
  };

  const handleImportFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so the same file can be re-chosen
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xlsm')) {
      toast.error('Please pick an .xlsx file');
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await subcontractAPI.importChallanItems(fd);
      const { traceability_type, items, row_count } = res.data || {};
      if (!items || items.length === 0) {
        toast.error('No valid rows found in the file');
        return;
      }
      // Map API rows → grid row shape used by the form
      const mapped = items.map(it => ({
        item_id: it.item_id,
        item_name: it.item_name,
        item_unique_id: it.item_unique_id,
        unit_of_measure: it.unit_of_measure,
        quantity: String(it.quantity),
        process_description: '',
        expected_return_date: '',
        work_order_number: it.trace_id || '',  // re-used for both work_order and challan_sr overrides
        raw_material_batch_number: it.raw_material_batch_number || '',
      }));
      setChallanItems(mapped);
      setChallanForm(f => ({ ...f, traceability_type: traceability_type || f.traceability_type }));
      toast.success(`Imported ${row_count} item${row_count === 1 ? '' : 's'} from Excel`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
        // Show first 3 errors in toast, full list in console
        const msg = detail.errors.slice(0, 3).join(' • ');
        toast.error(`Import failed: ${msg}${detail.errors.length > 3 ? ` (+${detail.errors.length - 3} more — see console)` : ''}`);
        console.error('Challan import validation errors:', detail.errors);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Import failed');
      }
    } finally {
      setImporting(false);
    }
  };

  const addExpectedOutput = () => {
    setExpectedOutputs(prev => [...prev, { item_id: '', quantity: '' }]);
  };

  const removeExpectedOutput = (idx) => {
    if (expectedOutputs.length <= 1) return;
    setExpectedOutputs(prev => prev.filter((_, i) => i !== idx));
  };

  const updateExpectedOutput = (idx, field, value) => {
    setExpectedOutputs(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  // Fetch and auto-populate recalled instruments for selected supplier
  const autoPopulateRecalled = async () => {
    if (!challanForm.supplier_id) {
      toast.error('Please select a supplier first');
      return;
    }
    setLoadingRecalled(true);
    try {
      const res = await subcontractAPI.getRecalledInstruments(challanForm.supplier_id);
      const recalled = res.data.recalled_instruments || [];
      if (recalled.length === 0) {
        toast.info('No recalled instruments found for this supplier (or all are already on outbound challans)');
        setRecalledCount(0);
        return;
      }
      // Filter instruments that have matching inventory items
      const withItems = recalled.filter(r => r.item);
      if (withItems.length === 0) {
        toast.warning(`Found ${recalled.length} recalled instruments but none are linked to inventory items`);
        setRecalledCount(0);
        return;
      }
      
      // Check for items with inventory warnings
      const itemsWithWarnings = withItems.filter(r => r.inventory_warning);
      const itemsOk = withItems.filter(r => !r.inventory_warning);
      
      if (itemsWithWarnings.length > 0 && itemsOk.length === 0) {
        // All items have inventory warnings
        toast.warning(`All ${withItems.length} instruments have no available inventory. Cannot add to challan.`);
        setRecalledCount(0);
        return;
      }
      
      // Add only items with available inventory
      const newItems = itemsOk.map(r => ({
        item_id: r.item.item_id,
        quantity: '1',
        process_description: 'Calibration',
        expected_return_date: '',
        instrument_code: r.instrument_code, // Extra info for display
      }));
      
      // Replace empty items or append
      if (challanItems.length === 1 && !challanItems[0].item_id) {
        setChallanItems(newItems);
      } else {
        setChallanItems(prev => [...prev, ...newItems]);
      }
      setRecalledCount(itemsOk.length);
      
      if (itemsWithWarnings.length > 0) {
        toast.warning(`Added ${itemsOk.length} instruments. Skipped ${itemsWithWarnings.length} with no available inventory.`);
      } else {
        toast.success(`Added ${itemsOk.length} recalled instruments for calibration`);
      }
    } catch (err) {
      toast.error('Failed to fetch recalled instruments');
    } finally {
      setLoadingRecalled(false);
    }
  };

  const handleCreate = async () => {
    if (challanForm.order_type === 'inbound') {
      if (!challanForm.customer_id) { toast.error('Customer is required for inbound'); return; }
      if (!challanForm.customer_challan_number?.trim()) { toast.error('Customer Challan Number is mandatory for inbound'); return; }
      // Validate work order numbers if traceability type is work_order
      if (challanForm.traceability_type === 'work_order') {
        const missingWO = challanItems.find(ci => ci.item_id && ci.quantity && !ci.work_order_number?.trim());
        if (missingWO) { toast.error('Work Order Number is required for each item when traceability is set to Work Order'); return; }
      }
    } else {
      if (!challanForm.supplier_id) { toast.error('Supplier is required'); return; }
    }
    const validItems = challanItems.filter(ci => ci.item_id && ci.quantity);
    if (validItems.length === 0) { toast.error('At least one item with quantity is required'); return; }

    setCreating(true);
    try {
      // Build PO number string from selected POs
      const selectedPONumbers = createAvailablePOs
        .filter(po => challanForm.purchase_order_ids.includes(po.id))
        .map(po => po.po_number)
        .join(', ');

      const payload = {
        ...challanForm,
        purchase_order_number: selectedPONumbers || challanForm.purchase_order_number,
        supplier_id: challanForm.order_type === 'inbound' ? challanForm.customer_id : challanForm.supplier_id,
        items: validItems.map((ci, idx) => {
          const item = {
            item_id: ci.item_id,
            quantity: parseFloat(ci.quantity),
            process_description: ci.process_description,
            expected_return_date: ci.expected_return_date,
          };
          // Add traceability info for inbound
          if (challanForm.order_type === 'inbound') {
            item.traceability_type = challanForm.traceability_type;
            item.customer_challan_number = challanForm.customer_challan_number;
            item.raw_material_batch_number = (ci.raw_material_batch_number || '').trim();
            if (challanForm.traceability_type === 'challan_sr') {
              // If the user imported a specific challan-sr value (held in work_order_number field), honour it.
              const override = (ci.work_order_number || '').trim();
              item.traceability_id = override || `${challanForm.customer_challan_number}-${idx + 1}`;
              item.traceability_id_description = 'Challan-Sr';
            } else if (challanForm.traceability_type === 'work_order') {
              item.traceability_id = ci.work_order_number;
              item.traceability_id_description = 'Work Order No';
            } else {
              item.traceability_id = '';
              item.traceability_id_description = 'Auto';
            }
          }
          return item;
        }),
      };

      // Add expected output items for inbound challans
      if (challanForm.order_type === 'inbound') {
        const validOutputs = expectedOutputs.filter(eo => eo.item_id && parseFloat(eo.quantity) > 0);
        payload.expected_output_items = validOutputs.map(eo => ({
          item_id: eo.item_id,
          quantity: parseFloat(eo.quantity),
        }));
      }

      // Remove customer_id from payload since backend doesn't have that field
      delete payload.customer_id;
      await subcontractAPI.createChallan(payload);
      toast.success('Challan created');
      setCreateOpen(false);
      loadChallans();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create challan'); }
    finally { setCreating(false); }
  };

  const handleSend = async (challanId) => {
    try {
      await subcontractAPI.sendChallan(challanId);
      toast.success('Challan sent');
      loadChallans();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
  };

  const handleDelete = async (challanId) => {
    if (!window.confirm('Are you sure you want to delete this draft challan?')) return;
    setDeleting(challanId);
    try {
      await subcontractAPI.deleteChallan(challanId);
      toast.success('Challan deleted');
      loadChallans();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete challan'); }
    finally { setDeleting(null); }
  };

  const openDetail = async (challanId) => {
    try {
      const res = await subcontractAPI.getChallan(challanId);
      setDetailChallan(res.data);
      setDetailOpen(true);
      // Load Job Work Items so the admin "Change Item" correction selector has options
      if (isAdmin && res.data?.order_type === 'inbound' && jobWorkItems.length === 0) {
        try {
          const jwi = await inventoryAPI.listJobWorkItems();
          setJobWorkItems(jwi.data || []);
        } catch { /* non-blocking */ }
      }
    } catch { toast.error('Failed to load challan details'); }
  };

  // Admin: correct an inbound challan entry (trace id / received-before-QC quantity)
  const [correctDlg, setCorrectDlg] = useState(null);
  const [correctSaving, setCorrectSaving] = useState(false);
  const saveCorrection = async () => {
    if (!correctDlg) return;
    if (correctDlg.field !== 'due' && !String(correctDlg.value ?? '').trim()) { toast.error('Please enter a value'); return; }
    if (!correctDlg.remarks.trim()) { toast.error('Reason for correction is required'); return; }
    if (correctDlg.field === 'qty' && !(parseFloat(correctDlg.value) > 0)) { toast.error('Quantity must be greater than zero'); return; }
    setCorrectSaving(true);
    try {
      if (correctDlg.field === 'trace') {
        await subcontractAPI.updateTraceability(correctDlg.challanId, {
          line_index: correctDlg.lineIndex, new_traceability_id: correctDlg.value.trim(), remarks: correctDlg.remarks.trim(),
        });
      } else if (correctDlg.field === 'item') {
        await subcontractAPI.correctChallanItem(correctDlg.challanId, {
          line_index: correctDlg.lineIndex, new_item_id: correctDlg.value, remarks: correctDlg.remarks.trim(),
        });
      } else if (correctDlg.field === 'due') {
        await subcontractAPI.correctItemDueDate(correctDlg.challanId, {
          line_index: correctDlg.lineIndex, due_date: correctDlg.value || '', remarks: correctDlg.remarks.trim(),
        });
      } else {
        await subcontractAPI.correctReceivedQty(correctDlg.challanId, {
          line_index: correctDlg.lineIndex, new_quantity: parseFloat(correctDlg.value), remarks: correctDlg.remarks.trim(),
        });
      }
      toast.success(correctDlg.field === 'item' ? 'Item changed — remember to reconcile physical stock' : 'Entry corrected');
      const cid = correctDlg.challanId;
      setCorrectDlg(null);
      await openDetail(cid);
      loadChallans();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to correct entry'); }
    finally { setCorrectSaving(false); }
  };

  // Send with Email functions
  const openSendWithEmail = async (challan) => {
    setSendEmailChallan(challan);
    setSendEmailForm({ send_email: true, email_to: '', email_cc: '', email_message: '' });
    setSendEmailOpen(true);
    
    // Load supplier email
    setLoadingSupplierEmail(true);
    try {
      const res = await subcontractAPI.getSupplierEmail(challan.id);
      setSendEmailForm(f => ({ ...f, email_to: res.data.supplier_email || '' }));
    } catch (err) {
      console.error('Failed to load supplier email', err);
    } finally {
      setLoadingSupplierEmail(false);
    }
  };

  const handleSendWithEmail = async () => {
    if (!sendEmailChallan) return;
    setSendingWithEmail(true);
    try {
      const res = await subcontractAPI.sendChallanWithEmail(sendEmailChallan.id, sendEmailForm);
      if (res.data.email_sent) {
        toast.success(`Challan sent and emailed to ${sendEmailForm.email_to}`);
      } else if (res.data.email_error) {
        toast.warning(`Challan sent but email failed: ${res.data.email_error}`);
      } else {
        toast.success('Challan sent successfully');
      }
      setSendEmailOpen(false);
      loadChallans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send challan');
    } finally {
      setSendingWithEmail(false);
    }
  };

  const handleSendOnly = async () => {
    if (!sendEmailChallan) return;
    setSendingWithEmail(true);
    try {
      await subcontractAPI.sendChallanWithEmail(sendEmailChallan.id, { send_email: false });
      toast.success('Challan sent successfully');
      setSendEmailOpen(false);
      loadChallans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send challan');
    } finally {
      setSendingWithEmail(false);
    }
  };

  const openReceive = async (challanId) => {
    try {
      const [challanRes, godownsRes] = await Promise.all([
        subcontractAPI.getChallan(challanId),
        inventoryAPI.listGodowns(),
      ]);
      const challan = challanRes.data;
      setGodowns(godownsRes.data || []);
      setReceiveChallan(challan);
      const hasBom = !!(challan.route_id || challan.route_snapshot);
      setReceiptMode(hasBom ? 'processed' : 'same_item'); // Default to processed for BOM challans
      setReceiveItems(
        (challan.items || []).map((ci, idx) => ({
          item_id: ci.item_id,
          item_name: ci.item_name,
          line_index: idx,
          receipt_type: 'same_item',
          sent_qty: ci.quantity,
          already_received: ci.received_quantity || 0,
          remaining_qty: Math.max(0, (ci.quantity || 0) - (ci.received_quantity || 0)),
          received_quantity: '',
          ok_quantity: '',
          rejected_quantity: '0',
          rework_quantity: '0',
          godown_id: '',
          traceability_id_description: '',
          traceability_seed: '',
          output_quantity: 1,
          sr_number: idx + 1,
        }))
      );
      setReceiveOpen(true);
    } catch { toast.error('Failed to load challan'); }
  };

  // Build receipt items based on current receipt mode
  const getReceiveItemsForMode = () => {
    if (!receiveChallan) return [];
    const hasBom = !!(receiveChallan.route_id || receiveChallan.route_snapshot);
    
    if (receiptMode === 'same_item' || receiptMode === 'rejected_rm' || receiptMode === 'return_as_is') {
      // Show sent items (challan.items) for same_item, rejected_rm, return_as_is
      return (receiveChallan.items || []).map((ci, idx) => ({
        item_id: ci.item_id,
        item_name: ci.item_name,
        line_index: idx,
        receipt_type: receiptMode,
        sent_qty: ci.quantity,
        already_received: ci.received_quantity || 0,
        remaining_qty: Math.max(0, (ci.quantity || 0) - (ci.received_quantity || 0)),
        received_quantity: '',
        ok_quantity: '',
        rejected_quantity: '0',
        rework_quantity: '0',
        godown_id: '',
        traceability_id_description: '',
        traceability_seed: '',
        output_quantity: 1,
        sr_number: idx + 1,
      }));
    } else if (receiptMode === 'processed' || receiptMode === 'scrap') {
      // Show expected output items (from BOM) for processed/scrap
      const outputs = receiveChallan.expected_output_items || [];
      const relevantOutputs = receiptMode === 'scrap' 
        ? outputs.filter(o => o.output_type === 'scrap')
        : outputs.filter(o => o.output_type !== 'scrap');
      return relevantOutputs.map((eo, idx) => ({
        item_id: eo.item_id,
        item_name: eo.item_name || eo.item_unique_id || '',
        line_index: null, // Not matching sent items
        receipt_type: receiptMode,
        expected_qty: eo.quantity || 0,
        already_received: eo.received_quantity || 0,
        remaining_qty: Math.max(0, (eo.quantity || 0) - (eo.received_quantity || 0)),
        received_quantity: '',
        ok_quantity: '',
        rejected_quantity: '0',
        rework_quantity: '0',
        godown_id: '',
        traceability_id_description: '',
        traceability_seed: '',
        output_quantity: 1,
        sr_number: idx + 1,
      }));
    }
    return [];
  };

  const updateReceiveItem = (idx, field, value) => {
    setReceiveItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const openDiscrepancy = async (challan) => {
    // Ensure godowns are loaded for proper godown resolution
    if (godowns.length === 0) {
      try {
        const godownsRes = await inventoryAPI.listGodowns();
        setGodowns(godownsRes.data || []);
      } catch { /* godowns will be empty, backend will auto-resolve */ }
    }
    setDiscrepancyChallan(challan);
    setDiscrepancyOpen(true);
  };

  const handleReceive = async () => {
    try {
      // Handle calibration challan first (different flow)
      if (receiveChallan?.is_calibration && receiveChallan?.calibration_tool_id) {
        const godownId = receiveItems[0]?.godown_id || '';
        if (!godownId) {
          toast.error('Please select a godown for receipt');
          return;
        }
        const res = await inventoryAPI.receiveFromCalibration({
          challan_id: receiveChallan.id,
          godown_id: godownId,
        });
        toast.success('Calibration instrument received');
        setReceiveOpen(false);
        loadChallans();
        // Redirect to calibration tool detail to update certificate
        if (res.data.redirect_to_calibration && res.data.tool_id) {
          toast.info('Redirecting to update calibration record...');
          setTimeout(() => navigate(`/calibration/tools/${res.data.tool_id}`), 1000);
        }
        return;
      }

      // Normal (non-calibration) receive flow
      const validItems = receiveItems.filter(ri => parseFloat(ri.received_quantity) > 0);
      if (validItems.length === 0) { toast.error('Enter received quantity for at least one item'); return; }

      // Validate godown is selected for all valid items (skip for auto-assigned types)
      const missingGodown = validItems.find(ri => 
        !ri.godown_id && !['rejected_rm', 'return_as_is', 'scrap'].includes(ri.receipt_type || receiptMode)
      );
      if (missingGodown) { toast.error('Please select a godown for all items'); return; }

      const payload = {
        items: validItems.map(ri => ({
          item_id: ri.item_id,
          line_index: ri.line_index,
          receipt_type: ri.receipt_type || receiptMode || 'same_item',
          received_quantity: parseFloat(ri.received_quantity),
          ok_quantity: parseFloat(ri.ok_quantity || ri.received_quantity || 0),
          rejected_quantity: parseFloat(ri.rejected_quantity || 0),
          rework_quantity: parseFloat(ri.rework_quantity || 0),
          godown_id: ri.godown_id,
          traceability_id_description: ri.traceability_id_description || '',
          traceability_id: ri.traceability_id || '',
          traceability_seed: ri.traceability_seed || '',
          output_quantity: parseInt(ri.output_quantity) || 1,
        })),
        remarks: '',
      };
      const res = await subcontractAPI.receiveChallan(receiveChallan.id, payload);
      toast.success('Receipt recorded');
      setReceiveOpen(false);
      loadChallans();

      // Check if calibration redirect is needed
      if (res.data?.calibration?.redirect_to_calibration && res.data?.calibration?.tool_id) {
        toast.info(res.data.calibration.message || 'Redirecting to update calibration record...');
        setTimeout(() => navigate(`/calibration/tools/${res.data.calibration.tool_id}`), 1000);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record receipt'); }
  };

  // ===== Service Entry & Closure Handlers =====
  const openServiceEntry = async (challan) => {
    setServiceEntryChallan(challan);
    setServiceForm({ invoice_number: '', invoice_date: '', service_item_name: 'Job Work Charges', ok_quantity: '', rate_per_unit: '', total_amount: '', remarks: '' });
    try {
      const res = await subcontractAPI.listServiceEntries(challan.id);
      setServiceEntries(res.data || []);
    } catch { setServiceEntries([]); }
    setServiceEntryOpen(true);
  };

  const handleSubmitServiceEntry = async () => {
    if (!serviceForm.invoice_number) { toast.error('Invoice number is required'); return; }
    setSubmittingService(true);
    try {
      await subcontractAPI.createServiceEntry(serviceEntryChallan.id, {
        ...serviceForm,
        ok_quantity: parseFloat(serviceForm.ok_quantity) || 0,
        rate_per_unit: parseFloat(serviceForm.rate_per_unit) || 0,
        total_amount: parseFloat(serviceForm.total_amount) || 0,
      });
      toast.success('Service entry recorded');
      const res = await subcontractAPI.listServiceEntries(serviceEntryChallan.id);
      setServiceEntries(res.data || []);
      setServiceForm({ invoice_number: '', invoice_date: '', service_item_name: 'Job Work Charges', ok_quantity: '', rate_per_unit: '', total_amount: '', remarks: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create service entry'); }
    setSubmittingService(false);
  };

  const handleCloseChallan = async (challan, forceClose = false, remarks = '') => {
    if (!forceClose && !confirm(`Close challan ${challan.challan_number}? This will also close linked POs.`)) return;
    setClosingChallan(true);
    try {
      const res = await subcontractAPI.closeOutboundChallan(challan.id, { force_close: forceClose, remarks });
      toast.success(res.data?.message || 'Challan closed');
      if (res.data?.po_auto_closed?.length > 0) {
        toast.success(`PO ${res.data.po_auto_closed.join(', ')} also closed`);
      }
      loadChallans();
      setServiceEntryOpen(false);
      setForceClosePrompt(null);
      setForceCloseRemarks('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to close challan'); }
    setClosingChallan(false);
  };

  const downloadPDF = async (challanId, challanNumber) => {
    try {
      const res = await subcontractAPI.downloadChallanPDF(challanId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DC_${challanNumber || challanId}.pdf`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      // Delay cleanup to allow download to start
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 1000);
      toast.success('PDF download started');
    } catch (err) {
      console.error('PDF download error:', err);
      toast.error('Failed to download challan PDF');
    }
  };

  // Inbound Dispatch Status
  const openDispatchStatus = async (challan) => {
    setDispatchStatus(null);
    setDispatchStatusOpen(true);
    setLoadingDispatchStatus(true);
    try {
      const res = await subcontractAPI.getInboundDispatchStatus(challan.id);
      setDispatchStatus(res.data);
    } catch (err) {
      toast.error('Failed to load dispatch status');
      setDispatchStatusOpen(false);
    } finally {
      setLoadingDispatchStatus(false);
    }
  };

  const handleCloseInbound = async (forceClose = false, remarks = '') => {
    if (!dispatchStatus) return;
    try {
      await subcontractAPI.closeInboundChallan(dispatchStatus.challan_id, { force_close: forceClose, remarks });
      toast.success('Inbound challan closed');
      setDispatchStatusOpen(false);
      setForceClosePrompt(null);
      setForceCloseRemarks('');
      loadChallans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close challan');
    }
  };

  const handleReopenChallan = async () => {
    if (!reopenPrompt || !reopenRemarks.trim()) return;
    setReopening(true);
    try {
      const res = await subcontractAPI.reopenChallan(reopenPrompt.challan.id, { remarks: reopenRemarks.trim() });
      toast.success(res.data?.message || 'Challan reopened');
      setReopenPrompt(null);
      setReopenRemarks('');
      setDetailOpen(false);
      setServiceEntryOpen(false);
      setDispatchStatusOpen(false);
      loadChallans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reopen challan');
    }
    setReopening(false);
  };

  const totalPages = Math.ceil(total / limit);

  // For outbound: use all items, for inbound: use only Job Work Items
  const itemOptions = allItems.map(i => ({
    value: i.id,
    label: `${i.unique_id} - ${i.item_name}`,
    sub: i.item_type,
    searchStr: `${i.unique_id} ${i.item_name} ${i.external_id || ''} ${i.category || ''}`,
  }));

  const jobWorkItemOptions = jobWorkItems.map(i => ({
    value: i.id,
    label: `${i.unique_id} - ${i.item_name}`,
    sub: 'Job Work Item',
    searchStr: `${i.unique_id} ${i.item_name} ${i.external_id || ''} ${i.category || ''}`,
  }));

  // Use appropriate items based on challan type
  const currentItemOptions = challanForm.order_type === 'inbound' ? jobWorkItemOptions : itemOptions;

  return (
    <div className="space-y-4" data-testid="subcontract-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">Subcontract Management</h1>
          <p className="text-xs text-muted-foreground">{total} challans</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleNotifyLowStockCustomers}
              disabled={notifyingLowStock}
              className="text-amber-700 border-amber-300 hover:bg-amber-50"
              data-testid="notify-low-stock-customers-btn"
              title="Send low-stock email now to customers for Job-Work raw material below reorder level"
            >
              {notifyingLowStock
                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                : <Mail className="w-4 h-4 mr-1" />}
              {notifyingLowStock ? 'Sending…' : 'Notify Low Stock'}
            </Button>
          )}
          <Button size="sm" onClick={() => openCreate('outbound')} data-testid="create-outbound-btn">
            <ArrowUpRight className="w-4 h-4 mr-1" /> Outbound Challan
          </Button>
          <Button size="sm" variant="outline" onClick={() => openCreate('inbound')} data-testid="create-inbound-btn">
            <ArrowDownLeft className="w-4 h-4 mr-1" /> Inbound Challan
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={v => { setActiveTab(v); setPage(1); }}>
        <TabsList>
          <TabsTrigger value="outbound" data-testid="tab-outbound">
            <ArrowUpRight className="w-3.5 h-3.5 mr-1" /> Outbound
          </TabsTrigger>
          <TabsTrigger value="inbound" data-testid="tab-inbound">
            <ArrowDownLeft className="w-3.5 h-3.5 mr-1" /> Inbound
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4 space-y-3">
          <Card>
            <CardContent className="p-3 flex gap-2 items-center">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search challan#, party, item..."
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                  className="h-8 text-xs pl-7"
                  data-testid="sc-search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-44 h-8 text-xs" data-testid="sc-status-filter">
                  <SelectValue placeholder="Open Challans" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open Challans</SelectItem>
                  <SelectItem value="all">All Challans</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="partially_received">Partially Received</SelectItem>
                  {activeTab === 'outbound' && <SelectItem value="all_received">All Received</SelectItem>}
                  {activeTab === 'inbound' && <SelectItem value="received">RM Received</SelectItem>}
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sc-challans-table">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Challan#</th>
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Supplier</th>
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Items</th>
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">PO#</th>
                    {activeTab === 'inbound' && <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Cust DC#</th>}
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Status</th>
                    <th className="text-left p-2.5 text-xs font-medium text-muted-foreground">Date</th>
                    <th className="text-right p-2.5 text-xs font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={activeTab === 'inbound' ? 8 : 7} className="text-center py-8 text-xs text-muted-foreground">Loading...</td></tr>
                  ) : challans.length === 0 ? (
                    <tr><td colSpan={activeTab === 'inbound' ? 8 : 7} className="text-center py-8 text-xs text-muted-foreground">No challans found</td></tr>
                  ) : challans.map(c => (
                    <tr key={c.id} className="border-b hover:bg-muted/50 transition-colors" data-testid={`challan-row-${c.challan_number}`}>
                      <td className="p-2.5 font-mono text-xs font-medium">
                        <button
                          className="text-blue-600 hover:underline cursor-pointer"
                          onClick={() => { setTrackingChallan(c); setTrackingOpen(true); }}
                          data-testid={`tracking-challan-${c.challan_number}`}
                        >
                          {c.challan_number}
                        </button>
                        {c.is_calibration && <Badge variant="outline" className="ml-1 text-[8px] bg-blue-50 text-blue-600 border-blue-200">CAL</Badge>}
                        {c.route_id && <Badge variant="outline" className="ml-1 text-[8px] bg-teal-50 text-teal-600 border-teal-200">BOM</Badge>}
                      </td>
                      <td className="p-2.5 text-xs">{c.supplier_name}</td>
                      <td className="p-2.5">
                        <div className="space-y-0.5">
                          {(c.items || []).slice(0, 2).map((ci, idx) => (
                            <p key={idx} className="text-xs truncate max-w-[180px]">
                              <EntityLink type="item" id={ci.item_id} className="font-mono text-[10px]">{ci.item_unique_id}</EntityLink> <EntityLink type="item" id={ci.item_id} className="text-xs">{ci.item_name}</EntityLink> <span className="text-muted-foreground">x{ci.quantity}</span>
                            </p>
                          ))}
                          {(c.items || []).length > 2 && (
                            <p className="text-[10px] text-muted-foreground">+{c.items.length - 2} more</p>
                          )}
                        </div>
                      </td>
                      <td className="p-2.5 text-xs text-muted-foreground">
                        {c.linked_pos && c.linked_pos.length > 0 ? (
                          <div className="flex flex-col gap-0.5">
                            {c.linked_pos.map((po, idx) => (
                              <span key={idx} className="font-mono text-blue-600">{po.po_number}</span>
                            ))}
                          </div>
                        ) : c.purchase_order_number || '-'}
                      </td>
                      {activeTab === 'inbound' && (
                        <td className="p-2.5 text-xs font-mono text-muted-foreground">{c.customer_challan_number || '-'}</td>
                      )}
                      <td className="p-2.5">
                        <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[c.status] || ''}`}>
                          {c.status.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="p-2.5 text-xs font-mono text-muted-foreground">{c.created_at?.slice(0, 10)}</td>
                      <td className="p-2.5 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => openDetail(c.id)} title="View Details" data-testid={`view-challan-${c.challan_number}`}>
                            <Eye className="w-3 h-3" />
                          </Button>
                          {/* Link PO button for outbound challans (not closed) */}
                          {activeTab === 'outbound' && !['closed', 'all_received'].includes(c.status) && (
                            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-1.5 text-violet-600 hover:text-violet-800" onClick={() => openLinkPO(c)} title="Link Purchase Order" data-testid={`link-po-${c.challan_number}`}>
                              <FileCheck className="w-3 h-3" />
                            </Button>
                          )}
                          {/* Link PO button for inbound challans (all statuses — retroactive PO linking for legacy) */}
                          {activeTab === 'inbound' && (
                            <Button size="sm" variant="ghost" className={`h-6 text-[10px] px-1.5 ${c.purchase_order_number ? 'text-green-600 hover:text-green-800' : 'text-violet-600 hover:text-violet-800'}`} onClick={() => openLinkPO(c)} title={c.purchase_order_number ? `PO: ${c.purchase_order_number} (click to change)` : 'Link Customer PO'} data-testid={`link-po-${c.challan_number}`}>
                              <FileCheck className="w-3 h-3" />
                            </Button>
                          )}
                          {c.status === 'draft' && (
                            <>
                              <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => openSendWithEmail(c)} data-testid={`send-challan-${c.challan_number}`}>
                                <Send className="w-3 h-3 mr-0.5" /> Send
                              </Button>
                              <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500 hover:text-red-700" onClick={() => handleDelete(c.id)} disabled={deleting === c.id} title="Delete Draft" data-testid={`delete-challan-${c.challan_number}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                          {['sent', 'partially_received', 'all_received'].includes(c.status) && activeTab !== 'inbound' && (
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => openReceive(c.id)} data-testid={`receive-challan-${c.challan_number}`}>
                              <PackageCheck className="w-3 h-3 mr-0.5" /> Receive
                            </Button>
                          )}
                          {/* Service Entry & Close for outbound challans */}
                          {activeTab === 'outbound' && ['all_received', 'partially_received', 'sent', 'closed'].includes(c.status) && (
                            <Button size="sm" variant="secondary" className="h-6 text-[10px] px-2" onClick={() => openServiceEntry(c)} data-testid={`service-entry-${c.challan_number}`}>
                              <FileCheck className="w-3 h-3 mr-0.5" /> {c.status === 'closed' ? 'Add Invoice' : 'Invoice / Close'}
                            </Button>
                          )}
                          {/* Close Challan / View Report for inbound challans */}
                          {activeTab === 'inbound' && ['sent', 'partially_received', 'received', 'closed'].includes(c.status) && (
                            <Button size="sm" variant={c.status === 'closed' ? 'outline' : 'secondary'} className="h-6 text-[10px] px-2" onClick={() => openDispatchStatus(c)} data-testid={`dispatch-status-${c.challan_number}`}>
                              <FileCheck className="w-3 h-3 mr-0.5" /> {c.status === 'closed' ? 'View Report' : 'Close Challan'}
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => downloadPDF(c.id, c.challan_number)} title="Download PDF" data-testid={`pdf-challan-${c.challan_number}`}>
                            <Download className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-3 border-t">
                <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs">
                    <ChevronLeft className="w-3 h-3 mr-1" /> Prev
                  </Button>
                  <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs">
                    Next <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* ===== CREATE CHALLAN DIALOG ===== */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {challanForm.order_type === 'outbound' ? 'New Outbound Challan' : 'New Inbound Challan'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Create a new {challanForm.order_type} subcontract challan
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {challanForm.order_type === 'outbound' ? (
                <div>
                  <Label className="text-xs">Supplier (Approved Only) *</Label>
                  <SearchableSelect
                    options={suppliers.map(s => ({ value: s.id, label: s.supplier_name, sub: s.supplier_code || '', searchStr: `${s.supplier_name} ${s.supplier_code || ''}` }))}
                    value={challanForm.supplier_id}
                    onSelect={v => { 
                      setChallanForm(f => ({ ...f, supplier_id: v, purchase_order_ids: [] })); 
                      setRecalledCount(0); 
                      loadPOsForSupplier(v);
                    }}
                    placeholder="Search suppliers..."
                    emptyText="No approved suppliers."
                    testId="sc-supplier-select"
                  />
                </div>
              ) : (
                <div>
                  <Label className="text-xs">Customer (Active Only) *</Label>
                  <SearchableSelect
                    options={customers.map(c => ({ value: c.id, label: c.customer_name, sub: c.customer_code || '', searchStr: `${c.customer_name} ${c.customer_code || ''} ${c.email || ''}` }))}
                    value={challanForm.customer_id}
                    onSelect={v => {
                      setChallanForm(f => ({ ...f, customer_id: v, purchase_order_ids: [], purchase_order_number: '', contract_po_id: '' }));
                      loadInboundCustomerPOs(v);
                    }}
                    placeholder="Search customers..."
                    emptyText="No active customers. Add in Contract Management."
                    testId="sc-customer-select"
                  />
                </div>
              )}
              <div>
                <Label className="text-xs">Due Date</Label>
                <Input type="date" value={challanForm.due_date}
                  onChange={e => setChallanForm(f => ({ ...f, due_date: e.target.value }))}
                  className="h-8 text-xs" data-testid="sc-due-date" />
              </div>
            </div>

            {/* PO Section — Outbound: pick from purchase_requests | Inbound: searchable customer PO dropdown */}
            {challanForm.order_type === 'inbound' && challanForm.customer_id && (
              <div className="p-3 border border-violet-200 rounded-lg bg-violet-50/30">
                <Label className="text-xs font-medium text-violet-800 flex items-center gap-1 mb-2">
                  <FileCheck className="w-3.5 h-3.5" />
                  Customer PO Number
                  {challanForm.purchase_order_number && (
                    <Badge variant="outline" className="ml-2 text-[10px] bg-violet-100 text-violet-700 border-violet-300">
                      {challanForm.purchase_order_number}
                      {challanForm.contract_po_id && <span className="ml-1 text-green-600">(Contract PO)</span>}
                    </Badge>
                  )}
                </Label>
                {loadingInboundPOs ? (
                  <div className="p-2 text-center text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading POs...
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Search + free-text input */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Search or type new PO number..."
                        value={inboundPOSearch}
                        onChange={e => {
                          setInboundPOSearch(e.target.value);
                          setInboundPODropdownOpen(true);
                        }}
                        onFocus={() => setInboundPODropdownOpen(true)}
                        className="h-8 text-xs flex-1"
                        data-testid="inbound-customer-po-input"
                      />
                      {inboundPOSearch && !inboundCustomerPOs.contract_pos.find(p => p.po_number === inboundPOSearch) && !inboundCustomerPOs.historical_pos.includes(inboundPOSearch) && (
                        <Button
                          type="button" size="sm" variant="outline" className="h-8 px-2 text-xs text-violet-700 border-violet-300"
                          onClick={() => {
                            setChallanForm(f => ({ ...f, purchase_order_number: inboundPOSearch, contract_po_id: '' }));
                            setInboundPODropdownOpen(false);
                          }}
                          data-testid="inbound-po-use-custom"
                        >
                          <Plus className="w-3 h-3 mr-1" /> Use "{inboundPOSearch}"
                        </Button>
                      )}
                    </div>
                    {/* Dropdown list */}
                    {inboundPODropdownOpen && (
                      <div className="max-h-40 overflow-y-auto border rounded bg-white shadow-sm" data-testid="inbound-po-dropdown">
                        {/* Contract POs (with items — enable BOM validation) */}
                        {inboundCustomerPOs.contract_pos.filter(po =>
                          !inboundPOSearch || po.po_number.toLowerCase().includes(inboundPOSearch.toLowerCase())
                        ).map(po => (
                          <div
                            key={po.id}
                            className={`px-2 py-1.5 border-b last:border-b-0 cursor-pointer hover:bg-violet-50 flex items-center justify-between text-xs ${challanForm.contract_po_id === po.id ? 'bg-violet-100' : ''}`}
                            onClick={() => {
                              setChallanForm(f => ({ ...f, purchase_order_number: po.po_number, contract_po_id: po.id }));
                              setInboundPOSearch(po.po_number);
                              setInboundPODropdownOpen(false);
                            }}
                            data-testid={`inbound-po-contract-${po.po_number}`}
                          >
                            <div>
                              <span className="font-mono font-medium">{po.po_number}</span>
                              <Badge variant="outline" className="ml-1.5 text-[9px] bg-green-50 text-green-700 border-green-200">{po.status}</Badge>
                              {po.items?.length > 0 && (
                                <span className="ml-1.5 text-[9px] text-muted-foreground">{po.items.length} item{po.items.length > 1 ? 's' : ''}</span>
                              )}
                            </div>
                            {challanForm.contract_po_id === po.id && <CheckCircle2 className="w-3.5 h-3.5 text-violet-600" />}
                          </div>
                        ))}
                        {/* Historical POs (text only — no BOM validation) */}
                        {inboundCustomerPOs.historical_pos.filter(po =>
                          !inboundPOSearch || po.toLowerCase().includes(inboundPOSearch.toLowerCase())
                        ).map(po => (
                          <div
                            key={po}
                            className={`px-2 py-1.5 border-b last:border-b-0 cursor-pointer hover:bg-violet-50 flex items-center justify-between text-xs ${challanForm.purchase_order_number === po && !challanForm.contract_po_id ? 'bg-violet-100' : ''}`}
                            onClick={() => {
                              setChallanForm(f => ({ ...f, purchase_order_number: po, contract_po_id: '' }));
                              setInboundPOSearch(po);
                              setInboundPODropdownOpen(false);
                            }}
                            data-testid={`inbound-po-history-${po}`}
                          >
                            <div>
                              <span className="font-mono font-medium">{po}</span>
                              <Badge variant="outline" className="ml-1.5 text-[9px] bg-slate-50 text-slate-600 border-slate-200">history</Badge>
                            </div>
                            {challanForm.purchase_order_number === po && !challanForm.contract_po_id && <CheckCircle2 className="w-3.5 h-3.5 text-violet-600" />}
                          </div>
                        ))}
                        {inboundCustomerPOs.contract_pos.length === 0 && inboundCustomerPOs.historical_pos.length === 0 && (
                          <div className="p-2 text-center text-xs text-muted-foreground">
                            No POs found. Type a new PO number above.
                          </div>
                        )}
                      </div>
                    )}
                    {/* BOM validation hint */}
                    {challanForm.contract_po_id && (
                      <p className="text-[10px] text-green-700 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Contract PO linked — BOM-to-PO item validation will be enforced on save
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            {challanForm.order_type === 'outbound' && challanForm.supplier_id && (
              <div className="p-3 border border-violet-200 rounded-lg bg-violet-50/30">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-medium text-violet-800 flex items-center gap-1">
                    <FileCheck className="w-3.5 h-3.5" />
                    Link Purchase Orders (Optional)
                  </Label>
                  {challanForm.purchase_order_ids.length > 0 && (
                    <Badge variant="outline" className="text-[10px] bg-violet-100 text-violet-700 border-violet-300">
                      {challanForm.purchase_order_ids.length} selected
                    </Badge>
                  )}
                </div>
                
                {/* PO Search */}
                <div className="flex gap-2 mb-2">
                  <Input
                    placeholder="Search POs..."
                    value={createPOSearch}
                    onChange={e => setCreatePOSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && loadPOsForSupplier(challanForm.supplier_id, createPOSearch)}
                    className="h-7 text-xs flex-1"
                    data-testid="create-po-search"
                  />
                  <Button type="button" size="sm" variant="outline" className="h-7 px-2" onClick={() => loadPOsForSupplier(challanForm.supplier_id, createPOSearch)}>
                    <Search className="w-3 h-3" />
                  </Button>
                </div>

                {/* PO List */}
                <div className="max-h-32 overflow-y-auto border rounded bg-white">
                  {loadingCreatePOs ? (
                    <div className="p-2 text-center text-xs text-muted-foreground">
                      <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading...
                    </div>
                  ) : createAvailablePOs.length === 0 ? (
                    <div className="p-2 text-center text-xs text-muted-foreground">
                      No POs found for this supplier
                    </div>
                  ) : (
                    createAvailablePOs.map(po => (
                      <div
                        key={po.id}
                        className={`px-2 py-1.5 border-b last:border-b-0 cursor-pointer hover:bg-violet-50 flex items-center justify-between text-xs ${challanForm.purchase_order_ids.includes(po.id) ? 'bg-violet-100' : ''}`}
                        onClick={() => toggleCreatePOSelection(po.id)}
                        data-testid={`create-po-${po.po_number}`}
                      >
                        <div>
                          <span className="font-mono font-medium">{po.po_number}</span>
                          <Badge variant="outline" className="ml-1.5 text-[9px]">{po.status}</Badge>
                        </div>
                        {challanForm.purchase_order_ids.includes(po.id) && (
                          <CheckCircle2 className="w-3.5 h-3.5 text-violet-600" />
                        )}
                      </div>
                    ))
                  )}
                </div>

                {/* Selected POs Preview */}
                {challanForm.purchase_order_ids.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {createAvailablePOs.filter(po => challanForm.purchase_order_ids.includes(po.id)).map(po => (
                      <Badge key={po.id} variant="outline" className="text-[10px] bg-white pr-1">
                        {po.po_number}
                        <button type="button" onClick={(e) => { e.stopPropagation(); toggleCreatePOSelection(po.id); }} className="ml-1 text-red-500 hover:text-red-700">
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* BOM/Route Linkage - Only for Outbound Manufacturing */}
            {challanForm.order_type === 'outbound' && challanForm.supplier_id && (
              <div className="p-3 border border-teal-200 rounded-lg bg-teal-50/30">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-medium text-teal-800 flex items-center gap-1">
                    <GitFork className="w-3.5 h-3.5" />
                    Link BOM / Route (Optional — for manufacturing jobs)
                  </Label>
                  {selectedRoute && (
                    <Badge variant="outline" className="text-[10px] bg-teal-100 text-teal-700 border-teal-300">
                      {selectedRoute.route_name}
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  <Select
                    value={challanForm.route_id || 'none'}
                    onValueChange={async (v) => {
                      const routeId = v === 'none' ? '' : v;
                      setChallanForm(f => ({ ...f, route_id: routeId }));
                      if (routeId) {
                        try {
                          const res = await inventoryAPI.getRoute(routeId);
                          setSelectedRoute(res.data);
                          // Auto-populate BOM input items as challan items
                          const bom = res.data?.bill_of_materials || [];
                          if (bom.length > 0) {
                            setChallanItems(bom.map(b => ({
                              item_id: b.item_id, quantity: String(b.quantity_per_unit || ''),
                              process_description: `For BOM: ${res.data.route_name}`, expected_return_date: '', work_order_number: '',
                            })));
                          }
                        } catch { toast.error('Failed to load route'); }
                      } else {
                        setSelectedRoute(null);
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs flex-1" data-testid="sc-route-select">
                      <SelectValue placeholder="Select BOM / Route..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (Calibration / Servicing)</SelectItem>
                      {routes.map(r => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.route_name} — {r.item_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" size="sm" variant="outline" className="h-8 px-2" onClick={async () => {
                    try {
                      const res = await inventoryAPI.listRoutes({ limit: 100, status: 'active' });
                      setRoutes(res.data.routes || []);
                    } catch { toast.error('Failed to load routes'); }
                  }} data-testid="load-routes-btn">
                    <Search className="w-3 h-3 mr-1" /> Load Routes
                  </Button>
                </div>
                {selectedRoute && (
                  <div className="mt-2 text-[10px] text-teal-700 space-y-1">
                    <p className="font-medium">Expected Outputs (per BOM):</p>
                    <div className="flex flex-wrap gap-1">
                      <Badge variant="outline" className="text-[9px] bg-green-50 border-green-200 text-green-700">
                        {selectedRoute.item_name} (Finished Good)
                      </Badge>
                      {(selectedRoute.output_items || []).map((oi, i) => (
                        <Badge key={i} variant="outline" className={`text-[9px] ${oi.output_type === 'scrap' ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
                          {oi.item_name || oi.item_id} ({oi.output_type})
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Inbound-specific: Customer Challan Number + Traceability Type */}
            {challanForm.order_type === 'inbound' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-blue-50/50 border border-blue-200 rounded-md">
                <div>
                  <Label className="text-xs font-semibold text-blue-800">Customer Challan Number *</Label>
                  <Input
                    value={challanForm.customer_challan_number}
                    onChange={e => setChallanForm(f => ({ ...f, customer_challan_number: e.target.value }))}
                    className="h-8 text-xs"
                    placeholder="Enter customer's challan/DC number"
                    data-testid="sc-customer-challan-number"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-blue-800">Traceability ID Type</Label>
                  <Select
                    value={challanForm.traceability_type}
                    onValueChange={v => setChallanForm(f => ({ ...f, traceability_type: v }))}
                  >
                    <SelectTrigger className="h-8 text-xs" data-testid="sc-traceability-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="challan_sr">Challan No + Sr Number</SelectItem>
                      <SelectItem value="work_order">Work Order Number</SelectItem>
                      <SelectItem value="auto">Auto-generate</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-blue-600 mt-0.5">
                    {challanForm.traceability_type === 'challan_sr' && challanForm.customer_challan_number
                      ? `IDs will be: ${challanForm.customer_challan_number}-1, ${challanForm.customer_challan_number}-2, ...`
                      : challanForm.traceability_type === 'work_order'
                        ? 'Enter WO number for each item below'
                        : 'System will auto-generate traceability IDs'}
                  </p>
                </div>
              </div>
            )}

            {/* Auto-populate recalled instruments button - only for outbound */}
            {challanForm.order_type === 'outbound' && challanForm.supplier_id && (
              <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-md border border-blue-200">
                <Wand2 className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-blue-700 flex-1">
                  Auto-populate recalled instruments for calibration
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={autoPopulateRecalled}
                  disabled={loadingRecalled}
                  className="h-7 text-[10px] bg-white border-blue-300 text-blue-700 hover:bg-blue-100"
                  data-testid="auto-populate-btn"
                >
                  {loadingRecalled ? (
                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Loading...</>
                  ) : (
                    <><Wand2 className="w-3 h-3 mr-1" /> Auto-Populate</>
                  )}
                </Button>
              </div>
            )}

            {/* Multi-item section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold">Items *</Label>
                <div className="flex items-center gap-2">
                  {challanForm.order_type === 'inbound' && (
                    <>
                      <input
                        type="file"
                        accept=".xlsx,.xlsm"
                        ref={importFileInputRef}
                        className="hidden"
                        onChange={handleImportFileChosen}
                        data-testid="challan-import-file-input"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleDownloadImportTemplate}
                        className="h-6 text-[10px]"
                        data-testid="download-import-template-btn"
                      >
                        <Download className="w-3 h-3 mr-0.5" /> Template
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => importFileInputRef.current?.click()}
                        disabled={importing}
                        className="h-6 text-[10px]"
                        data-testid="import-items-excel-btn"
                      >
                        {importing
                          ? <Loader2 className="w-3 h-3 mr-0.5 animate-spin" />
                          : <Upload className="w-3 h-3 mr-0.5" />}
                        {importing ? 'Importing…' : 'Import Excel'}
                      </Button>
                    </>
                  )}
                  <Button type="button" size="sm" variant="outline" onClick={addChallanItem} className="h-6 text-[10px]" data-testid="add-item-btn">
                    <Plus className="w-3 h-3 mr-0.5" /> Add Item
                  </Button>
                </div>
              </div>
              <div className="space-y-3">
                {challanForm.order_type === 'inbound' && jobWorkItemOptions.length === 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-700">
                    <strong>Note:</strong> No Job Work Items found. Only items with type "Job Work Item" can be used for inbound subcontracting. 
                    Create Job Work Items in the Item Master first.
                  </div>
                )}
                {challanItems.map((ci, idx) => (
                  <Card key={idx} className="p-3 bg-muted/30">
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <Label className="text-[10px] text-muted-foreground">
                            Item {challanForm.order_type === 'inbound' && <span className="text-amber-600">(Job Work Items only)</span>}
                          </Label>
                          <SearchableSelect
                            options={currentItemOptions}
                            value={ci.item_id}
                            onSelect={v => updateChallanItem(idx, 'item_id', v)}
                            placeholder={challanForm.order_type === 'inbound' ? "Search job work items..." : "Search items..."}
                            emptyText={challanForm.order_type === 'inbound' ? "No Job Work Items found." : "No items."}
                            testId={`sc-item-${idx}`}
                          />
                        </div>
                        {challanItems.length > 1 && (
                          <Button type="button" size="icon" variant="ghost" className="h-7 w-7 mt-4 text-red-500" onClick={() => removeChallanItem(idx)} data-testid={`remove-item-${idx}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Qty *</Label>
                          <Input type="number" value={ci.quantity}
                            onChange={e => updateChallanItem(idx, 'quantity', e.target.value)}
                            className="h-7 text-xs" data-testid={`sc-qty-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Process</Label>
                          <Input value={ci.process_description}
                            onChange={e => updateChallanItem(idx, 'process_description', e.target.value)}
                            className="h-7 text-xs" placeholder="e.g. Machining" data-testid={`sc-process-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Expected Return</Label>
                          <Input type="date" value={ci.expected_return_date}
                            onChange={e => updateChallanItem(idx, 'expected_return_date', e.target.value)}
                            className="h-7 text-xs" data-testid={`sc-return-${idx}`} />
                        </div>
                      </div>
                      {/* Per-item traceability fields for inbound */}
                      {challanForm.order_type === 'inbound' && (
                        <div className="mt-2 pt-2 border-t border-dashed space-y-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-muted-foreground">Traceability ID:</span>
                            {challanForm.traceability_type === 'challan_sr' ? (
                              <Badge variant="outline" className="text-[10px] font-mono bg-blue-50 text-blue-700">
                                {challanForm.customer_challan_number ? `${challanForm.customer_challan_number}-${idx + 1}` : 'Enter challan# above'}
                              </Badge>
                            ) : challanForm.traceability_type === 'work_order' ? (
                              <div className="flex-1">
                                <Input
                                  value={ci.work_order_number || ''}
                                  onChange={e => updateChallanItem(idx, 'work_order_number', e.target.value)}
                                  className="h-6 text-xs"
                                  placeholder="Enter Work Order Number *"
                                  data-testid={`sc-wo-number-${idx}`}
                                />
                              </div>
                            ) : (
                              <Badge variant="outline" className="text-[10px] text-muted-foreground">Auto-generated on receipt</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-muted-foreground whitespace-nowrap">RM Lot / Batch No.:</span>
                            <Input
                              value={ci.raw_material_batch_number || ''}
                              onChange={e => updateChallanItem(idx, 'raw_material_batch_number', e.target.value)}
                              className="h-6 text-xs flex-1"
                              placeholder="Customer's raw-material lot/batch (optional, printed on COC)"
                              data-testid={`sc-rm-batch-${idx}`}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Expected Output Items (Inbound only) */}
            {challanForm.order_type === 'inbound' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label className="text-xs font-semibold">Expected Finished Goods Output</Label>
                    <p className="text-[10px] text-muted-foreground">What FG items will be produced from the raw material above?</p>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={addExpectedOutput} className="h-6 text-[10px]" data-testid="add-expected-output-btn">
                    <Plus className="w-3 h-3 mr-0.5" /> Add FG
                  </Button>
                </div>
                <div className="space-y-2">
                  {expectedOutputs.map((eo, idx) => (
                    <Card key={idx} className="p-3 bg-emerald-50/50 border-emerald-200">
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <Label className="text-[10px] text-muted-foreground">Finished Good Item</Label>
                          <SearchableSelect
                            options={finishedGoodItems.map(i => ({
                              value: i.id,
                              label: `${i.unique_id} - ${i.item_name}`,
                              sub: i.item_type,
                              searchStr: `${i.unique_id} ${i.item_name} ${i.external_id || ''} ${i.category || ''}`,
                            }))}
                            value={eo.item_id}
                            onSelect={v => updateExpectedOutput(idx, 'item_id', v)}
                            placeholder="Search FG items..."
                            emptyText="No items found."
                            testId={`expected-output-item-${idx}`}
                          />
                        </div>
                        <div className="w-24">
                          <Label className="text-[10px] text-muted-foreground">Qty</Label>
                          <Input type="number" value={eo.quantity}
                            onChange={e => updateExpectedOutput(idx, 'quantity', e.target.value)}
                            className="h-7 text-xs" data-testid={`expected-output-qty-${idx}`} />
                        </div>
                        {expectedOutputs.length > 1 && (
                          <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => removeExpectedOutput(idx)} data-testid={`remove-expected-output-${idx}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs">Remarks</Label>
              <Textarea value={challanForm.remarks}
                onChange={e => setChallanForm(f => ({ ...f, remarks: e.target.value }))}
                className="text-xs min-h-[40px]" data-testid="sc-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="save-challan-btn">
              {creating ? 'Creating...' : 'Create Challan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== DETAIL DIALOG ===== */}
      {/* Admin: correct inbound challan entry (trace id / received-before-QC qty) */}
      <Dialog open={!!correctDlg} onOpenChange={(o) => !o && setCorrectDlg(null)}>
        <DialogContent className="max-w-md" data-testid="correct-entry-dialog">
          <DialogHeader>
            <DialogTitle>
              {correctDlg?.field === 'trace' ? 'Correct Work Order / Trace ID'
                : correctDlg?.field === 'item' ? 'Change Item (Wrong Item Correction)'
                : correctDlg?.field === 'due' ? 'Add / Change Due Date'
                : 'Correct Quantity Received'}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {correctDlg?.item?.item_name}{correctDlg?.item?.item_unique_id ? ` (${correctDlg.item.item_unique_id})` : ''}
              {correctDlg?.field === 'qty' && ' — editable only before QC inspection.'}
            </DialogDescription>
          </DialogHeader>
          {correctDlg && (
            <div className="space-y-3">
              {correctDlg.field === 'item' && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-[11px] text-amber-900 space-y-1" data-testid="correct-item-warning">
                  <p className="font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Important — 3 things happen when you change the item:</p>
                  <p>1. The item on this challan line is replaced.</p>
                  <p>2. QC records update <b>automatically</b> (they read this challan live) — no manual QC edit needed.</p>
                  <p>3. <b>Physical stock</b> received under the old item must be corrected manually via <b>Stock Reconciliation</b> (move qty from the old item to the new item).</p>
                  <p className="text-amber-700">Allowed only while production for this line has not been completed.</p>
                </div>
              )}
              {correctDlg.field === 'item' ? (
                <div className="space-y-1.5">
                  <Label className="text-xs">New Item <span className="text-amber-600">(Job Work Items)</span></Label>
                  <SearchableSelect
                    options={jobWorkItemOptions}
                    value={correctDlg.value}
                    onSelect={(v) => setCorrectDlg({ ...correctDlg, value: v })}
                    placeholder="Search job work items..."
                    emptyText="No Job Work Items found."
                    testId="correct-item-select"
                  />
                </div>
              ) : correctDlg.field === 'due' ? (
                <div className="space-y-1.5">
                  <Label className="text-xs">Due Date</Label>
                  <Input
                    type="date"
                    value={correctDlg.value}
                    onChange={(e) => setCorrectDlg({ ...correctDlg, value: e.target.value })}
                    data-testid="correct-entry-value"
                    autoFocus
                  />
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label className="text-xs">{correctDlg.field === 'trace' ? 'Work Order Number / Trace ID' : 'Quantity Received'}</Label>
                  <Input
                    type={correctDlg.field === 'qty' ? 'number' : 'text'}
                    value={correctDlg.value}
                    onChange={(e) => setCorrectDlg({ ...correctDlg, value: e.target.value })}
                    data-testid="correct-entry-value"
                    autoFocus
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs">Reason for correction <span className="text-red-600">*</span></Label>
                <Textarea
                  value={correctDlg.remarks}
                  onChange={(e) => setCorrectDlg({ ...correctDlg, remarks: e.target.value })}
                  placeholder="e.g. Wrong item selected at challan entry"
                  className="text-sm"
                  rows={2}
                  data-testid="correct-entry-remarks"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrectDlg(null)} data-testid="correct-entry-cancel">Cancel</Button>
            <Button onClick={saveCorrection} disabled={correctSaving} data-testid="correct-entry-save">
              {correctSaving ? 'Saving...' : 'Save Correction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Challan: {detailChallan?.challan_number}</DialogTitle>
            <DialogDescription className="sr-only">Challan details</DialogDescription>
          </DialogHeader>
          {detailChallan && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-muted-foreground">Supplier:</span> <span className="font-medium">{detailChallan.supplier_name}</span></div>
                <div><span className="text-muted-foreground">Status:</span> <Badge variant="outline" className={`text-[10px] ml-1 ${STATUS_COLORS[detailChallan.status]}`}>{detailChallan.status.replace(/_/g, ' ')}</Badge></div>
                <div><span className="text-muted-foreground">Type:</span> <span className="font-medium capitalize">{detailChallan.order_type}</span></div>
                <div><span className="text-muted-foreground">PO#:</span> <span className="font-medium">{detailChallan.purchase_order_number || '-'}</span></div>
                {detailChallan.order_type === 'inbound' && (
                  <div><span className="text-muted-foreground">Customer DC#:</span> <span className="font-medium font-mono">{detailChallan.customer_challan_number || '-'}</span></div>
                )}
                <div><span className="text-muted-foreground">Created:</span> <span className="font-mono">{detailChallan.created_at?.slice(0, 10)}</span></div>
                {detailChallan.remarks && <div className="col-span-2"><span className="text-muted-foreground">Remarks:</span> {detailChallan.remarks}</div>}
                {detailChallan.close_remarks && (
                  <div className="col-span-2 p-2 bg-amber-50 border border-amber-200 rounded">
                    <span className="text-muted-foreground">Close Remarks{detailChallan.force_closed ? ' (Force Closed)' : ''}:</span>{' '}
                    <span className="text-amber-800">{detailChallan.close_remarks}</span>
                    {detailChallan.closed_by_name && <span className="text-[10px] text-muted-foreground ml-2">— {detailChallan.closed_by_name}</span>}
                  </div>
                )}
                {detailChallan.reopen_remarks && (
                  <div className="col-span-2 p-2 bg-blue-50 border border-blue-200 rounded">
                    <span className="text-muted-foreground">Reopened:</span>{' '}
                    <span className="text-blue-800">{detailChallan.reopen_remarks}</span>
                    {detailChallan.reopened_by_name && <span className="text-[10px] text-muted-foreground ml-2">— {detailChallan.reopened_by_name}, {detailChallan.reopened_at?.slice(0,10)}</span>}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-xs font-semibold mb-2">Items ({detailChallan.items?.length || 0})</h3>
                <table className="w-full text-xs border">
                  <thead>
                    <tr className="bg-muted/50 border-b">
                      <th className="text-left p-2 font-medium">Item</th>
                      <th className="text-left p-2 font-medium">Trace #</th>
                      <th className="text-left p-2 font-medium">Process</th>
                      <th className="text-center p-2 font-medium">Sent</th>
                      <th className="text-center p-2 font-medium">Received</th>
                      <th className="text-center p-2 font-medium">OK</th>
                      <th className="text-center p-2 font-medium">Rejected</th>
                      <th className="text-left p-2 font-medium">Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detailChallan.items || []).map((ci, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="p-2">
                          <div className="flex items-start gap-1">
                            <div>
                              <p className="font-medium">{ci.item_name}</p>
                              <p className="font-mono text-[10px] text-muted-foreground">{ci.item_unique_id}</p>
                            </div>
                            {isAdmin && detailChallan.order_type === 'inbound' && detailChallan.status !== 'closed' && (
                              <Button
                                size="icon" variant="ghost" className="h-5 w-5 shrink-0 text-muted-foreground hover:text-amber-700"
                                title="Change item (wrong item correction — before production)"
                                data-testid={`edit-item-${idx}`}
                                onClick={() => setCorrectDlg({ challanId: detailChallan.id, lineIndex: idx, field: 'item', item: ci, value: '', remarks: '' })}
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="p-2">
                          <div className="flex items-start gap-1">
                            {ci.traceability_id ? (
                              <div>
                                <p className="font-mono font-medium text-blue-700">{ci.traceability_id}</p>
                                <p className="text-[9px] text-muted-foreground">{ci.traceability_id_description || ''}</p>
                              </div>
                            ) : <span>-</span>}
                            {isAdmin && detailChallan.order_type === 'inbound' && detailChallan.status !== 'closed' && (
                              <Button
                                size="icon" variant="ghost" className="h-5 w-5 shrink-0 text-muted-foreground hover:text-blue-700"
                                title="Correct work order / trace ID"
                                data-testid={`edit-trace-${idx}`}
                                onClick={() => setCorrectDlg({ challanId: detailChallan.id, lineIndex: idx, field: 'trace', item: ci, value: ci.traceability_id || '', remarks: '' })}
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="p-2 text-muted-foreground">{ci.process_description || '-'}</td>
                        <td className="p-2 text-center font-medium">
                          <div className="flex items-center justify-center gap-1">
                            <span>{ci.quantity}</span>
                            {isAdmin && detailChallan.order_type === 'inbound' && detailChallan.status !== 'closed' && (ci.received_quantity || 0) === 0 && (
                              <Button
                                size="icon" variant="ghost" className="h-5 w-5 shrink-0 text-muted-foreground hover:text-blue-700"
                                title="Correct quantity received (before QC inspection)"
                                data-testid={`edit-qty-${idx}`}
                                onClick={() => setCorrectDlg({ challanId: detailChallan.id, lineIndex: idx, field: 'qty', item: ci, value: String(ci.quantity ?? ''), remarks: '' })}
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="p-2 text-center">{ci.received_quantity || 0}</td>
                        <td className="p-2 text-center text-emerald-600">{ci.ok_quantity || 0}</td>
                        <td className="p-2 text-center text-red-600">{ci.rejected_quantity || 0}</td>
                        <td className="p-2 font-mono text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <span>{ci.expected_return_date || '-'}</span>
                            {isAdmin && detailChallan.order_type === 'inbound' && detailChallan.status !== 'closed' && (
                              <Button
                                size="icon" variant="ghost" className="h-5 w-5 shrink-0 text-muted-foreground hover:text-blue-700"
                                title="Add / change due date"
                                data-testid={`edit-due-${idx}`}
                                onClick={() => setCorrectDlg({ challanId: detailChallan.id, lineIndex: idx, field: 'due', item: ci, value: ci.expected_return_date || '', remarks: '' })}
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Expected Output Items (for inbound challans) */}
              {detailChallan.order_type === 'inbound' && (detailChallan.expected_output_items || []).length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold mb-2">Expected Finished Goods Output</h3>
                  <div className="space-y-1">
                    {detailChallan.expected_output_items.map((eo, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-emerald-50 rounded border border-emerald-200">
                        <div className="text-xs">
                          <span className="font-mono text-[10px] text-muted-foreground">{eo.item_unique_id}</span>{' '}
                          <span className="font-medium">{eo.item_name}</span>
                        </div>
                        <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300">
                          x{eo.quantity} {eo.unit_of_measure || 'Nos'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}


              {(detailChallan.receipts || []).length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold mb-2">Receipt History</h3>
                  <div className="space-y-2">
                    {detailChallan.receipts.map(r => (
                      <Card key={r.id} className="p-2">
                        <p className="text-[10px] text-muted-foreground mb-1">
                          {new Date(r.received_date).toLocaleDateString()} by {r.received_by_name}
                        </p>
                        <div className="text-xs space-y-0.5">
                          {(r.items || []).map((ri, idx) => (
                            <p key={idx}>{ri.item_name}: Rcvd {ri.received_quantity} (OK: {ri.ok_quantity}, Rej: {ri.rejected_quantity})</p>
                          ))}
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Report Short/Excess Button */}
              {detailChallan && ['sent', 'partially_received', 'received'].includes(detailChallan.status) && (
                <Button variant="outline" size="sm" className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => { setDetailOpen(false); openDiscrepancy(detailChallan); }}
                  data-testid="report-discrepancy-btn">
                  <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
                  Report Short / Excess Qty
                </Button>
              )}

              {/* Reopen Closed Challan — Admin Only */}
              {detailChallan && detailChallan.status === 'closed' && isAdmin && (
                <Button variant="outline" size="sm" className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                  onClick={() => { setDetailOpen(false); setReopenPrompt({ challan: detailChallan }); }}
                  data-testid="reopen-challan-btn">
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                  Reopen Challan
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== RECEIVE DIALOG ===== */}
      <Dialog open={receiveOpen} onOpenChange={setReceiveOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{receiveChallan?.order_type === 'inbound' ? 'Receive Raw Material' : 'Receive Against'}: {receiveChallan?.challan_number}
              {receiveChallan?.is_calibration && <Badge variant="outline" className="ml-2 text-[10px] bg-blue-50 text-blue-700">Calibration</Badge>}
            </DialogTitle>
            <DialogDescription className="sr-only">Receive items against challan</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Receipt Type Tabs — only for BOM-linked outbound challans */}
            {receiveChallan?.order_type === 'outbound' && (receiveChallan?.route_id || receiveChallan?.route_snapshot) && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Receipt Type</Label>
                <div className="flex flex-wrap gap-1 p-1 bg-muted/30 rounded-md" data-testid="receipt-type-tabs">
                  {[
                    { key: 'processed', label: 'Processed Goods', color: 'green', desc: 'Finished goods per BOM' },
                    { key: 'scrap', label: 'Scrap', color: 'amber', desc: 'Scrap per BOM' },
                    { key: 'rejected_rm', label: 'Rejected RM', color: 'red', desc: 'Rejected raw material → Quarantine' },
                    { key: 'return_as_is', label: 'Return As-Is', color: 'blue', desc: 'Raw material returned → Store' },
                  ].map(rt => (
                    <button
                      key={rt.key}
                      className={`px-3 py-1.5 text-[10px] rounded transition-colors ${receiptMode === rt.key ? `bg-${rt.color}-100 text-${rt.color}-800 font-medium shadow border border-${rt.color}-300` : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}
                      onClick={() => {
                        setReceiptMode(rt.key);
                      }}
                      data-testid={`receipt-type-${rt.key}`}
                    >
                      {rt.label}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground">
                  {receiptMode === 'processed' && 'Receive finished goods from BOM outputs. Goes through QC if configured.'}
                  {receiptMode === 'scrap' && 'Receive scrap from BOM outputs. Direct to SCRAP godown.'}
                  {receiptMode === 'rejected_rm' && 'Rejected raw material back from supplier. Will be placed in QUARANTINE and trigger MRB.'}
                  {receiptMode === 'return_as_is' && 'Raw material returned without processing. Will be placed back in STORE.'}
                </p>
              </div>
            )}
            {/* Non-BOM outbound: simple same-item receipt */}
            {receiveChallan?.order_type === 'outbound' && !receiveChallan?.route_id && !receiveChallan?.route_snapshot && !receiveChallan?.is_calibration && (
              <p className="text-[10px] text-muted-foreground bg-blue-50 p-2 rounded border border-blue-200">
                Same items sent will be received back. Stock will increase on receipt.
              </p>
            )}

            {receiveChallan?.is_calibration && (
              <div>
                <Label className="text-xs">Receive into Godown *</Label>
                <SearchableSelect
                  options={godowns.map(g => ({ value: g.id, label: `${g.code} - ${g.name}`, sub: g.description || '', searchStr: `${g.code} ${g.name}` }))}
                  value={receiveItems[0]?.godown_id || ''}
                  onSelect={v => setReceiveItems(prev => prev.map(ri => ({ ...ri, godown_id: v })))}
                  placeholder="Select godown..."
                  emptyText="No godowns."
                  testId="recv-godown-select"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Inventory will be incremented and calibration certificate flow will open.</p>
              </div>
            )}
            {receiveItems.map((ri, idx) => (
              <Card key={idx} className={`p-3 ${ri.remaining_qty <= 0 ? 'bg-green-50/50 opacity-60' : 'bg-muted/30'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-medium">{ri.item_name}</p>
                    {ri.receipt_type && ri.receipt_type !== 'same_item' && (
                      <Badge variant="outline" className={`text-[8px] px-1 py-0 ${
                        ri.receipt_type === 'processed' ? 'bg-green-50 text-green-700 border-green-200' :
                        ri.receipt_type === 'scrap' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        ri.receipt_type === 'rejected_rm' ? 'bg-red-50 text-red-700 border-red-200' :
                        'bg-blue-50 text-blue-700 border-blue-200'
                      }`}>{ri.receipt_type === 'processed' ? 'Processed' : ri.receipt_type === 'scrap' ? 'Scrap' : ri.receipt_type === 'rejected_rm' ? 'Rejected RM' : 'Return As-Is'}</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-[10px] text-muted-foreground">
                      {ri.sent_qty != null ? `Sent: ${ri.sent_qty} | ` : ri.expected_qty != null ? `Expected: ${ri.expected_qty} | ` : ''}
                      Rcvd: {ri.already_received} | <span className={ri.remaining_qty > 0 ? 'text-amber-600 font-medium' : 'text-green-600 font-medium'}>Remaining: {ri.remaining_qty}</span>
                    </p>
                    {ri.remaining_qty <= 0 && <Badge variant="outline" className="text-[8px] px-1 py-0 bg-green-100 text-green-700 border-green-300">Fully Received</Badge>}
                  </div>
                </div>
                {ri.remaining_qty > 0 && !receiveChallan?.is_calibration && (
                  <>
                    <div className={`grid gap-2 ${(ri.receipt_type === 'rejected_rm' || ri.receipt_type === 'return_as_is' || ri.receipt_type === 'scrap') ? 'grid-cols-2' : 'grid-cols-5'}`}>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Received Qty</Label>
                        <Input type="number" value={ri.received_quantity}
                          onChange={e => {
                            updateReceiveItem(idx, 'received_quantity', e.target.value);
                            // For rejected_rm/return_as_is/scrap, ok_qty = received_qty
                            if (ri.receipt_type === 'rejected_rm' || ri.receipt_type === 'return_as_is' || ri.receipt_type === 'scrap') {
                              updateReceiveItem(idx, 'ok_quantity', e.target.value);
                            }
                          }}
                          className="h-7 text-xs" data-testid={`recv-qty-${idx}`} />
                      </div>
                      {!(ri.receipt_type === 'rejected_rm' || ri.receipt_type === 'return_as_is' || ri.receipt_type === 'scrap') && (
                        <>
                          <div>
                            <Label className="text-[10px] text-muted-foreground">OK Qty</Label>
                            <Input type="number" value={ri.ok_quantity}
                              onChange={e => updateReceiveItem(idx, 'ok_quantity', e.target.value)}
                              className="h-7 text-xs" data-testid={`recv-ok-${idx}`} />
                          </div>
                          <div>
                            <Label className="text-[10px] text-muted-foreground">Rejected</Label>
                            <Input type="number" value={ri.rejected_quantity}
                              onChange={e => updateReceiveItem(idx, 'rejected_quantity', e.target.value)}
                              className="h-7 text-xs" data-testid={`recv-rej-${idx}`} />
                          </div>
                          <div>
                            <Label className="text-[10px] text-muted-foreground">Rework</Label>
                            <Input type="number" value={ri.rework_quantity}
                              onChange={e => updateReceiveItem(idx, 'rework_quantity', e.target.value)}
                              className="h-7 text-xs" data-testid={`recv-rework-${idx}`} />
                          </div>
                        </>
                      )}
                      <div>
                        <Label className="text-[10px] text-muted-foreground">Godown *</Label>
                        {(ri.receipt_type === 'rejected_rm' || ri.receipt_type === 'return_as_is' || ri.receipt_type === 'scrap') ? (
                          <div className="h-7 flex items-center px-2 rounded border bg-muted/50 text-xs text-muted-foreground" data-testid={`recv-godown-fixed-${idx}`}>
                            {ri.receipt_type === 'rejected_rm' ? 'QUARANTINE (auto)' : ri.receipt_type === 'scrap' ? 'SCRAP (auto)' : 'STORE (auto)'}
                          </div>
                        ) : (
                          <Select value={ri.godown_id || ''} onValueChange={v => updateReceiveItem(idx, 'godown_id', v)}>
                            <SelectTrigger className="h-7 text-xs" data-testid={`recv-godown-${idx}`}>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {godowns.map(g => (
                                <SelectItem key={g.id} value={g.id}>{g.code} - {g.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>
                    {/* Traceability Fields for Inbound Job Work */}
                    {receiveChallan?.order_type === 'inbound' && (
                      <div className="mt-2 pt-2 border-t border-dashed">
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-[10px] text-muted-foreground">Traceability ID Type</Label>
                            <Select 
                              value={ri.traceability_id_description || '_auto'} 
                              onValueChange={v => updateReceiveItem(idx, 'traceability_id_description', v === '_auto' ? '' : v)}
                            >
                              <SelectTrigger className="h-7 text-xs" data-testid={`recv-trace-type-${idx}`}>
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_auto">Auto-generate</SelectItem>
                                <SelectItem value="Work Order No">Work Order No</SelectItem>
                                <SelectItem value="Serial No">Serial No</SelectItem>
                                <SelectItem value="Batch No">Batch No</SelectItem>
                                <SelectItem value="Customer ID">Customer ID</SelectItem>
                                <SelectItem value="Other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-[10px] text-muted-foreground">
                              Traceability Seed ID {!ri.traceability_seed && <span className="text-amber-600">(auto)</span>}
                            </Label>
                            <Input 
                              value={ri.traceability_seed}
                              onChange={e => updateReceiveItem(idx, 'traceability_seed', e.target.value)}
                              className="h-7 text-xs" 
                              placeholder={`e.g., WO-12345 or auto: ${receiveChallan?.challan_number}-${ri.sr_number}`}
                              data-testid={`recv-trace-seed-${idx}`} 
                            />
                          </div>
                          <div>
                            <Label className="text-[10px] text-muted-foreground">Output Parts</Label>
                            <Input 
                              type="number"
                              min="1"
                              value={ri.output_quantity}
                              onChange={e => updateReceiveItem(idx, 'output_quantity', e.target.value)}
                              className="h-7 text-xs" 
                              placeholder="1"
                              data-testid={`recv-output-qty-${idx}`} 
                            />
                          </div>
                        </div>
                        {ri.traceability_seed && parseInt(ri.output_quantity) > 1 && (
                          <p className="text-[10px] text-blue-600 mt-1">
                            IDs will be: {ri.traceability_seed}-1, {ri.traceability_seed}-2, ... {ri.traceability_seed}-{ri.output_quantity}
                          </p>
                        )}
                        {!ri.traceability_seed && parseInt(ri.output_quantity) > 1 && (
                          <p className="text-[10px] text-amber-600 mt-1">
                            Auto IDs: {receiveChallan?.challan_number}-{ri.sr_number}-1, {receiveChallan?.challan_number}-{ri.sr_number}-2, ... -{ri.output_quantity}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </Card>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveOpen(false)}>Cancel</Button>
            <Button onClick={handleReceive} data-testid="save-receipt-btn">
              {receiveChallan?.is_calibration ? 'Receive & Open Calibration' : 'Record Receipt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service Entry & Close Dialog (Outbound) */}
      <Dialog open={serviceEntryOpen} onOpenChange={setServiceEntryOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <FileCheck className="w-4 h-4" />
              Service Entry / Invoice: {serviceEntryChallan?.challan_number}
            </DialogTitle>
            <DialogDescription className="sr-only">Record service entry for challan</DialogDescription>
          </DialogHeader>
          {serviceEntryChallan && (
            <div className="space-y-4">
              {/* Receipt Summary */}
              <div className="grid grid-cols-3 gap-3">
                <Card className="p-3">
                  <p className="text-[10px] text-muted-foreground">Status</p>
                  <Badge className={`text-[10px] ${STATUS_COLORS[serviceEntryChallan.status] || ''}`} data-testid="se-status">
                    {serviceEntryChallan.status?.replace(/_/g, ' ')}
                  </Badge>
                </Card>
                <Card className="p-3">
                  <p className="text-[10px] text-muted-foreground">Total OK Received</p>
                  <p className="text-sm font-semibold" data-testid="se-ok-qty">
                    {(() => {
                      const okQty = (serviceEntryChallan.items || []).reduce((s, ci) => s + (ci.ok_quantity || 0), 0);
                      const processedQty = (serviceEntryChallan.expected_output_items || [])
                        .filter(eo => eo.output_type !== 'scrap')
                        .reduce((s, eo) => s + (eo.received_quantity || 0), 0);
                      return Math.max(okQty, processedQty);
                    })()}
                  </p>
                </Card>
                <Card className="p-3">
                  <p className="text-[10px] text-muted-foreground">Already Invoiced</p>
                  <p className="text-sm font-semibold" data-testid="se-invoiced-qty">
                    {serviceEntries.reduce((s, se) => s + (se.ok_quantity || 0), 0)}
                  </p>
                </Card>
              </div>

              {/* Existing Service Entries */}
              {serviceEntries.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium mb-2">Existing Invoices</h4>
                  <div className="space-y-1.5">
                    {serviceEntries.map(se => (
                      <div key={se.id} className="flex items-center justify-between p-2 bg-muted/30 rounded-md text-[11px]">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="text-[10px]">{se.invoice_number}</Badge>
                          <span>Qty: {se.ok_quantity}</span>
                          <span>Amount: {se.total_amount?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</span>
                        </div>
                        <span className="text-muted-foreground">{se.invoice_date || new Date(se.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Service Entry Form */}
              <div className="p-3 border rounded-lg space-y-3">
                <h4 className="text-xs font-medium">Record New Invoice / Service Entry</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px]">Invoice Number *</Label>
                    <Input value={serviceForm.invoice_number} onChange={e => setServiceForm(f => ({ ...f, invoice_number: e.target.value }))}
                      className="h-8 text-xs" placeholder="INV-001" data-testid="se-invoice-number" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Invoice Date</Label>
                    <Input type="date" value={serviceForm.invoice_date} onChange={e => setServiceForm(f => ({ ...f, invoice_date: e.target.value }))}
                      className="h-8 text-xs" data-testid="se-invoice-date" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Service Description</Label>
                    <Input value={serviceForm.service_item_name} onChange={e => setServiceForm(f => ({ ...f, service_item_name: e.target.value }))}
                      className="h-8 text-xs" placeholder="Job Work Charges" data-testid="se-service-name" />
                  </div>
                  <div>
                    <Label className="text-[10px]">OK Quantity (Invoiceable)</Label>
                    <Input type="number" value={serviceForm.ok_quantity} onChange={e => setServiceForm(f => ({ ...f, ok_quantity: e.target.value }))}
                      className="h-8 text-xs" data-testid="se-ok-qty-input" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Rate per Unit</Label>
                    <Input type="number" value={serviceForm.rate_per_unit} onChange={e => {
                      const rate = parseFloat(e.target.value) || 0;
                      const qty = parseFloat(serviceForm.ok_quantity) || 0;
                      setServiceForm(f => ({ ...f, rate_per_unit: e.target.value, total_amount: String(rate * qty) }));
                    }} className="h-8 text-xs" data-testid="se-rate" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Total Amount (INR)</Label>
                    <Input type="number" value={serviceForm.total_amount} onChange={e => setServiceForm(f => ({ ...f, total_amount: e.target.value }))}
                      className="h-8 text-xs" data-testid="se-total-amount" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px]">Remarks</Label>
                  <Input value={serviceForm.remarks} onChange={e => setServiceForm(f => ({ ...f, remarks: e.target.value }))}
                    className="h-8 text-xs" placeholder="Optional remarks" data-testid="se-remarks" />
                </div>
                <Button size="sm" onClick={handleSubmitServiceEntry} disabled={submittingService} className="w-full h-8 text-xs" data-testid="se-submit-btn">
                  {submittingService ? 'Submitting...' : 'Record Service Entry'}
                </Button>
              </div>

              {/* Close Challan */}
              <div className="flex gap-2 pt-2 border-t">
                {serviceEntryChallan.status === 'all_received' && (
                  <Button onClick={() => handleCloseChallan(serviceEntryChallan)} disabled={closingChallan} className="flex-1 h-8 text-xs" data-testid="close-challan-btn">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {closingChallan ? 'Closing...' : 'Close Challan & PO'}
                  </Button>
                )}
                {isAdmin && ['sent', 'partially_received'].includes(serviceEntryChallan.status) && (
                  <Button variant="outline" onClick={() => setForceClosePrompt({ challan: serviceEntryChallan, type: 'outbound' })} disabled={closingChallan} className="flex-1 h-8 text-xs text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="force-close-btn">
                    <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Force Close (Legacy)
                  </Button>
                )}
                <Button variant="outline" onClick={() => setServiceEntryOpen(false)} className="h-8 text-xs">Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Close Challan Dialog (Inbound) */}
      <Dialog open={dispatchStatusOpen} onOpenChange={setDispatchStatusOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <FileCheck className="w-4 h-4" />
              {dispatchStatus?.status === 'closed' ? 'Challan Report' : 'Close Challan'}: {dispatchStatus?.challan_number}
            </DialogTitle>
            <DialogDescription className="sr-only">Close outbound challan</DialogDescription>
          </DialogHeader>
          {loadingDispatchStatus ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : dispatchStatus ? (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-3">
                <Card className="p-3">
                  <p className="text-[10px] text-muted-foreground">Challan Status</p>
                  <Badge variant="outline" className="mt-1">{dispatchStatus.status}</Badge>
                </Card>
                <Card className="p-3">
                  <p className="text-[10px] text-muted-foreground">Customer</p>
                  <p className="text-sm font-medium truncate">{dispatchStatus.customer_name}</p>
                </Card>
                <Card className="p-3">
                  <p className="text-[10px] text-muted-foreground">Closure Status</p>
                  <Badge variant={dispatchStatus.can_close ? 'default' : 'secondary'} className={`mt-1 ${dispatchStatus.can_close ? 'bg-green-600' : ''}`}>
                    {dispatchStatus.can_close ? 'Ready to Close' : 'Pending'}
                  </Badge>
                  {!dispatchStatus.can_close && dispatchStatus.close_reason && (
                    <p className="text-[10px] text-amber-600 mt-1">{dispatchStatus.close_reason}</p>
                  )}
                </Card>
              </div>
              {/* DC Number & Customer PO */}
              {(dispatchStatus.challan_number || dispatchStatus.purchase_order_number) && (
                <div className="grid grid-cols-2 gap-3">
                  <Card className="p-3">
                    <p className="text-[10px] text-muted-foreground">Inbound DC Number</p>
                    <p className="text-sm font-mono font-medium">{dispatchStatus.challan_number || '-'}</p>
                  </Card>
                  <Card className="p-3">
                    <p className="text-[10px] text-muted-foreground">Customer PO</p>
                    <p className="text-sm font-mono font-medium">{dispatchStatus.purchase_order_number || '-'}</p>
                  </Card>
                </div>
              )}

              {/* Raw Materials Received */}
              {dispatchStatus.raw_materials?.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium mb-2 text-muted-foreground">Raw Materials Received</h4>
                  <div className="space-y-1">
                    {dispatchStatus.raw_materials.map((rm, idx) => (
                      <div key={idx} className="p-2 border rounded bg-blue-50/50 text-xs flex justify-between items-center">
                        <div>
                          <span className="font-medium">{rm.item_name}</span>
                          {rm.traceability_id && <span className="ml-2 text-blue-600 font-mono text-[10px]">Trace: {rm.traceability_id}</span>}
                          {rm.work_order_number && <span className="ml-2 text-violet-600 font-mono text-[10px]">WO: {rm.work_order_number}</span>}
                        </div>
                        <span>Qty: <b>{rm.received_quantity}</b></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* RM Accountability - Material accounting */}
              {dispatchStatus.rm_accountability?.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium mb-2">Material Accountability</h4>
                  <div className="space-y-2">
                    {dispatchStatus.rm_accountability.map((rm, idx) => (
                      <Card key={idx} className="p-3 bg-slate-50/50">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-medium">{rm.item_name}</span>
                          {rm.line_count > 1 && <span className="text-[10px] text-muted-foreground">({rm.line_count} lines)</span>}
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-[10px]">
                          <div className="p-1.5 bg-blue-50 rounded text-center">
                            <p className="text-muted-foreground">Received</p>
                            <p className="font-bold text-blue-700">{rm.received_qty}</p>
                          </div>
                          <div className="p-1.5 bg-violet-50 rounded text-center">
                            <p className="text-muted-foreground">Consumed (WO)</p>
                            <p className="font-bold text-violet-700">{rm.consumed_by_wo}</p>
                          </div>
                          <div className="p-1.5 bg-amber-50 rounded text-center">
                            <p className="text-muted-foreground">Returned (as-is)</p>
                            <p className="font-bold text-amber-700">{rm.returned_as_is}</p>
                          </div>
                          <div className={`p-1.5 rounded text-center ${rm.unaccounted_qty > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                            <p className="text-muted-foreground">Unaccounted</p>
                            <p className={`font-bold ${rm.unaccounted_qty > 0 ? 'text-red-700' : 'text-green-700'}`}>{rm.unaccounted_qty}</p>
                          </div>
                        </div>
                        {/* Progress bar */}
                        {rm.received_qty > 0 && (
                          <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                            <div className={`h-1.5 rounded-full ${rm.unaccounted_qty <= 0 ? 'bg-green-500' : 'bg-amber-500'}`}
                              style={{ width: `${Math.min(100, ((rm.consumed_by_wo + rm.returned_as_is) / rm.received_qty) * 100)}%` }} />
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* WO Consumption History */}
              {dispatchStatus.wo_consumption?.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium mb-2">Work Order Consumption</h4>
                  <div className="space-y-1">
                    {dispatchStatus.wo_consumption.map((wo, idx) => (
                      <div key={idx} className="p-2 border rounded text-xs flex justify-between items-center">
                        <span className="font-mono text-[10px]">{wo.wo_number}</span>
                        <span className="font-medium">{wo.fg_item_name}</span>
                        <span>Planned: {wo.planned_qty}</span>
                        <span className="text-green-600">Produced: {wo.produced_qty}</span>
                        {wo.scrap_qty > 0 && <span className="text-amber-600">Scrap: {wo.scrap_qty}</span>}
                        <Badge variant="outline" className="text-[10px]">{wo.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* FG Dispatch Status */}
              <div>
                <h4 className="text-xs font-medium mb-2">FG Dispatch Status</h4>
                {dispatchStatus.fg_utilization?.length > 0 ? (
                  <div className="space-y-2">
                    {dispatchStatus.fg_utilization.map((item, idx) => (
                      <div key={idx} className="p-3 border rounded bg-muted/30">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="text-xs font-medium">{item.item_name}</span>
                            {item.item_unique_id && <span className="text-[10px] text-muted-foreground ml-2">{item.item_unique_id}</span>}
                            {item.note && <span className="text-[10px] text-blue-600 ml-2">({item.note})</span>}
                          </div>
                          <div className="flex gap-3 text-xs">
                            {item.expected_qty > 0 && <span>Expected: <b>{item.expected_qty}</b></span>}
                            <span className="text-green-600">Dispatched: <b>{item.dispatched_qty}</b></span>
                            {item.pending_qty > 0 && (
                              <span className="text-amber-600">Pending: <b>{item.pending_qty}</b></span>
                            )}
                            {item.pending_qty <= 0 && item.dispatched_qty > 0 && (
                              <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                            )}
                          </div>
                        </div>
                        {item.expected_qty > 0 && (
                          <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                            <div className={`h-1.5 rounded-full ${item.pending_qty <= 0 ? 'bg-green-500' : 'bg-amber-500'}`}
                              style={{ width: `${Math.min(100, (item.dispatched_qty / item.expected_qty) * 100)}%` }} />
                          </div>
                        )}
                        {item.dispatch_challans?.length > 0 && (
                          <div className="mt-2 pl-2 border-l-2 border-green-200">
                            {item.dispatch_challans.map((dc, dcIdx) => (
                              <p key={dcIdx} className="text-[10px] text-muted-foreground">
                                {dc.dc_number}: {dc.quantity} units ({dc.date})
                                {dc.reconciliation_status && (
                                  <Badge variant={dc.reconciliation_status === 'reconciled' ? 'default' : 'secondary'} className="ml-1 text-[8px]">
                                    {dc.reconciliation_status}
                                  </Badge>
                                )}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground p-3 border rounded">No FG items expected. Material may be returned as-is or work orders not yet created.</p>
                )}
              </div>

              {/* RM Returns (as-is) */}
              {dispatchStatus.rm_returns?.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium mb-2">RM Returns (as-is)</h4>
                  <div className="space-y-1">
                    {dispatchStatus.rm_returns.map((rm, idx) => (
                      <div key={idx} className="p-2 border rounded bg-amber-50/50 text-xs">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">{rm.item_name}</span>
                          <span>Returned: <b>{rm.returned_qty}</b></span>
                        </div>
                        {rm.dispatch_challans?.length > 0 && (
                          <div className="mt-1 pl-2 border-l-2 border-amber-200">
                            {rm.dispatch_challans.map((dc, dcIdx) => (
                              <p key={dcIdx} className="text-[10px] text-muted-foreground">
                                {dc.dc_number}: {dc.quantity} units ({dc.date})
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Scrap Summary */}
              {dispatchStatus.scrap_summary && dispatchStatus.scrap_summary.total_produced > 0 && (
                <div>
                  <h4 className="text-xs font-medium mb-2">Scrap Summary</h4>
                  <Card className="p-3 bg-amber-50 border-amber-200">
                    <div className="flex gap-4 text-xs">
                      <span>Produced: <b>{dispatchStatus.scrap_summary.total_produced}</b></span>
                      <span className="text-green-600">Returned: <b>{dispatchStatus.scrap_summary.dispatched}</b></span>
                      <span className={dispatchStatus.scrap_summary.pending > 0 ? 'text-amber-600' : 'text-green-600'}>
                        Pending: <b>{dispatchStatus.scrap_summary.pending}</b>
                      </span>
                    </div>
                  </Card>
                </div>
              )}

              {/* Dispatch Challans */}
              {dispatchStatus.dispatches?.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium mb-2">All Dispatch Challans</h4>
                  <div className="space-y-1">
                    {dispatchStatus.dispatches.map((dc, idx) => (
                      <div key={idx} className="p-2 border rounded text-xs flex justify-between items-center">
                        <span className="font-mono">{dc.dc_number}</span>
                        <span>{dc.total_items || dc.items?.length || 0} items</span>
                        <span>{(dc.dispatch_date || dc.created_at || '').slice(0, 10)}</span>
                        <Badge variant={dc.reconciliation_status === 'reconciled' ? 'default' : 'secondary'} className="text-[10px]">
                          {dc.reconciliation_status === 'reconciled' ? (dc.invoice_number || 'Reconciled') : 'Pending'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter className="mt-4 flex gap-2">
            <Button variant="outline" onClick={() => setDispatchStatusOpen(false)}>Cancel</Button>
            {dispatchStatus?.status === 'closed' && isAdmin && (
              <Button 
                variant="outline"
                onClick={() => { setDispatchStatusOpen(false); setReopenPrompt({ challan: { id: dispatchStatus.challan_id, challan_number: dispatchStatus.challan_number } }); }}
                className="text-blue-700 border-blue-300 hover:bg-blue-50"
                data-testid="reopen-inbound-btn"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen
              </Button>
            )}
            {dispatchStatus?.status !== 'closed' && !dispatchStatus?.can_close && isAdmin && (
              <Button 
                variant="outline"
                onClick={() => setForceClosePrompt({ challan: { challan_number: dispatchStatus?.challan_number }, type: 'inbound' })}
                className="text-amber-700 border-amber-300 hover:bg-amber-50"
                data-testid="force-close-inbound-btn"
              >
                <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Force Close
              </Button>
            )}
            <Button 
              onClick={() => handleCloseInbound()} 
              disabled={!dispatchStatus?.can_close && dispatchStatus?.status !== 'closed'}
              data-testid="close-inbound-btn"
            >
              <FileCheck className="w-3 h-3 mr-1" />
              {dispatchStatus?.status === 'closed' ? 'Already Closed' : 'Close Challan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Challan Tracking Dialog */}
      <ChallanTrackingDialog
        open={trackingOpen}
        onOpenChange={setTrackingOpen}
        challan={trackingChallan}
      />

      {/* Link PO Dialog */}
      <Dialog open={linkPOOpen} onOpenChange={(open) => { if (!open) setLinkPOOpen(false); }}>
        <DialogContent className="max-w-lg" data-testid="link-po-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-violet-600" />
              Link Purchase Order
            </DialogTitle>
            <DialogDescription className="sr-only">Link a purchase order to this challan</DialogDescription>
          </DialogHeader>
          
          {linkPOChallan && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg border">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium">{linkPOChallan.challan_number}</p>
                    <p className="text-xs text-muted-foreground">{linkPOChallan.supplier_name}</p>
                    {linkPOChallan.order_type === 'inbound' && linkPOChallan.purchase_order_number && (
                      <p className="text-[10px] text-violet-600 mt-0.5">Current PO: {linkPOChallan.purchase_order_number}</p>
                    )}
                  </div>
                  <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[linkPOChallan.status]}`}>
                    {linkPOChallan.status?.replace(/_/g, ' ')}
                  </Badge>
                </div>
              </div>

              {/* INBOUND MODE: Customer PO selection */}
              {linkPOChallan.order_type === 'inbound' ? (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Search or type Customer PO</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        placeholder="Search or type new PO number..."
                        value={linkInboundPOSearch}
                        onChange={e => setLinkInboundPOSearch(e.target.value)}
                        className="h-8 text-xs flex-1"
                        data-testid="link-inbound-po-search"
                      />
                      {linkInboundPOSearch && !linkInboundPOs.contract_pos.find(p => p.po_number === linkInboundPOSearch) && !linkInboundPOs.historical_pos.includes(linkInboundPOSearch) && (
                        <Button type="button" size="sm" variant="outline" className="h-8 px-2 text-xs"
                          onClick={() => { setLinkInboundSelectedPO(linkInboundPOSearch); setLinkInboundContractPOId(''); }}
                          data-testid="link-inbound-use-custom"
                        >
                          <Plus className="w-3 h-3 mr-1" /> Use
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    {loadingPOs ? (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" /> Loading...
                      </div>
                    ) : (
                      <>
                        {linkInboundPOs.contract_pos.filter(po =>
                          !linkInboundPOSearch || po.po_number.toLowerCase().includes(linkInboundPOSearch.toLowerCase())
                        ).map(po => (
                          <div key={po.id}
                            className={`p-2.5 border-b last:border-b-0 cursor-pointer hover:bg-violet-50 flex items-center justify-between ${linkInboundContractPOId === po.id ? 'bg-violet-100' : ''}`}
                            onClick={() => { setLinkInboundSelectedPO(po.po_number); setLinkInboundContractPOId(po.id); setLinkInboundPOSearch(po.po_number); }}
                            data-testid={`link-po-contract-${po.po_number}`}
                          >
                            <div>
                              <p className="text-xs font-medium font-mono">{po.po_number}</p>
                              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                <Badge variant="outline" className="text-[9px] bg-green-50 text-green-700 border-green-200">{po.status}</Badge>
                                {po.items?.length > 0 && `${po.items.length} item${po.items.length > 1 ? 's' : ''}`}
                              </span>
                            </div>
                            {linkInboundContractPOId === po.id && <CheckCircle2 className="w-4 h-4 text-violet-600" />}
                          </div>
                        ))}
                        {linkInboundPOs.historical_pos.filter(po =>
                          !linkInboundPOSearch || po.toLowerCase().includes(linkInboundPOSearch.toLowerCase())
                        ).map(po => (
                          <div key={po}
                            className={`p-2.5 border-b last:border-b-0 cursor-pointer hover:bg-violet-50 flex items-center justify-between ${linkInboundSelectedPO === po && !linkInboundContractPOId ? 'bg-violet-100' : ''}`}
                            onClick={() => { setLinkInboundSelectedPO(po); setLinkInboundContractPOId(''); setLinkInboundPOSearch(po); }}
                            data-testid={`link-po-history-${po}`}
                          >
                            <div>
                              <p className="text-xs font-medium font-mono">{po}</p>
                              <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-600 border-slate-200">history</Badge>
                            </div>
                            {linkInboundSelectedPO === po && !linkInboundContractPOId && <CheckCircle2 className="w-4 h-4 text-violet-600" />}
                          </div>
                        ))}
                        {linkInboundPOs.contract_pos.length === 0 && linkInboundPOs.historical_pos.length === 0 && (
                          <div className="p-4 text-center text-sm text-muted-foreground">No POs found. Type a new PO above.</div>
                        )}
                      </>
                    )}
                  </div>
                  {linkInboundSelectedPO && (
                    <div className="p-2 bg-violet-50 rounded-lg border border-violet-200">
                      <p className="text-[10px] font-medium text-violet-700">
                        Selected: <span className="font-mono">{linkInboundSelectedPO}</span>
                        {linkInboundContractPOId && <span className="text-green-600 ml-1">(Contract PO)</span>}
                      </p>
                    </div>
                  )}
                  {/* Clear PO option */}
                  {(linkPOChallan.purchase_order_number || linkInboundSelectedPO) && (
                    <Button type="button" size="sm" variant="ghost" className="h-7 text-[10px] text-red-500 hover:text-red-700"
                      onClick={() => { setLinkInboundSelectedPO(''); setLinkInboundContractPOId(''); setLinkInboundPOSearch(''); }}
                      data-testid="link-po-clear"
                    >
                      <X className="w-3 h-3 mr-1" /> Clear PO Link
                    </Button>
                  )}
                </div>
              ) : (
                /* OUTBOUND MODE: Supplier PO selection (existing behavior) */
                <>
                  <div>
                    <Label className="text-xs">Search Purchase Orders</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        placeholder="Search by PO number or supplier..."
                        value={poSearchTerm}
                        onChange={e => setPOSearchTerm(e.target.value)}
                        className="h-8 text-xs"
                        onKeyDown={e => e.key === 'Enter' && loadAvailablePOs(linkPOChallan.supplier_id, poSearchTerm)}
                        data-testid="po-search-input"
                      />
                      <Button size="sm" variant="outline" className="h-8" onClick={() => loadAvailablePOs(linkPOChallan.supplier_id, poSearchTerm)}>
                        <Search className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Available Purchase Orders</Label>
                    <div className="mt-1 border rounded-lg max-h-48 overflow-y-auto">
                      {loadingPOs ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" /> Loading...
                        </div>
                      ) : availablePOs.length === 0 ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">No purchase orders found</div>
                      ) : (
                        availablePOs.map(po => (
                          <div key={po.id}
                            className={`p-2.5 border-b last:border-b-0 cursor-pointer hover:bg-slate-50 flex items-center justify-between ${selectedPOs.includes(po.id) ? 'bg-violet-50' : ''}`}
                            onClick={() => togglePOSelection(po.id)}
                            data-testid={`po-option-${po.po_number}`}
                          >
                            <div>
                              <p className="text-xs font-medium font-mono">{po.po_number}</p>
                              <p className="text-[10px] text-muted-foreground">{po.supplier_name}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-[10px]">{po.status}</Badge>
                              {selectedPOs.includes(po.id) && <CheckCircle2 className="w-4 h-4 text-violet-600" />}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  {selectedPOs.length > 0 && (
                    <div className="p-2 bg-violet-50 rounded-lg border border-violet-200">
                      <p className="text-[10px] font-medium text-violet-700 mb-1">Selected POs ({selectedPOs.length}):</p>
                      <div className="flex flex-wrap gap-1">
                        {availablePOs.filter(po => selectedPOs.includes(po.id)).map(po => (
                          <Badge key={po.id} variant="outline" className="text-[10px] bg-white">
                            {po.po_number}
                            <button onClick={(e) => { e.stopPropagation(); togglePOSelection(po.id); }} className="ml-1 text-red-500 hover:text-red-700">
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setLinkPOOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={savePOLink} disabled={linkingPO} data-testid="save-po-link-btn">
              {linkingPO ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
              Save PO Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send with Email Dialog */}
      <Dialog open={sendEmailOpen} onOpenChange={(open) => { if (!open) setSendEmailOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="send-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-600" />
              Send Challan
            </DialogTitle>
            <DialogDescription className="sr-only">Send challan via email</DialogDescription>
          </DialogHeader>
          
          {sendEmailChallan && (
            <div className="space-y-4">
              {/* Challan Info */}
              <div className="p-3 bg-slate-50 rounded-lg border">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium font-mono">{sendEmailChallan.challan_number}</p>
                    <p className="text-xs text-muted-foreground">{sendEmailChallan.supplier_name}</p>
                    {sendEmailChallan.purchase_order_number && (
                      <p className="text-xs text-blue-600 font-mono mt-0.5">PO: {sendEmailChallan.purchase_order_number}</p>
                    )}
                  </div>
                  <Badge variant="outline" className="text-[10px]">
                    {sendEmailChallan.items?.length || 0} items
                  </Badge>
                </div>
              </div>

              {/* Email Option */}
              <div className="p-3 border rounded-lg bg-blue-50/30 border-blue-200">
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    id="send_email"
                    checked={sendEmailForm.send_email}
                    onChange={e => setSendEmailForm(f => ({ ...f, send_email: e.target.checked }))}
                    className="rounded border-blue-300"
                  />
                  <Label htmlFor="send_email" className="text-xs font-medium text-blue-800 flex items-center gap-1 cursor-pointer">
                    <Mail className="w-3.5 h-3.5" />
                    Email challan PDF to supplier
                  </Label>
                </div>

                {sendEmailForm.send_email && (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-[10px] text-blue-700">Supplier Email *</Label>
                      {loadingSupplierEmail ? (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground py-1">
                          <Loader2 className="w-3 h-3 animate-spin" /> Loading...
                        </div>
                      ) : (
                        <Input
                          type="email"
                          value={sendEmailForm.email_to}
                          onChange={e => setSendEmailForm(f => ({ ...f, email_to: e.target.value }))}
                          placeholder="supplier@example.com"
                          className="h-7 text-xs"
                          data-testid="send-email-to"
                        />
                      )}
                    </div>
                    <div>
                      <Label className="text-[10px] text-blue-700">CC (comma separated)</Label>
                      <Input
                        type="text"
                        value={sendEmailForm.email_cc}
                        onChange={e => setSendEmailForm(f => ({ ...f, email_cc: e.target.value }))}
                        placeholder="cc1@example.com, cc2@example.com"
                        className="h-7 text-xs"
                        data-testid="send-email-cc"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] text-blue-700">Message (optional)</Label>
                      <Textarea
                        value={sendEmailForm.email_message}
                        onChange={e => setSendEmailForm(f => ({ ...f, email_message: e.target.value }))}
                        placeholder="Please find attached the outbound challan..."
                        className="text-xs min-h-[60px]"
                        data-testid="send-email-message"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSendEmailOpen(false)}>Cancel</Button>
            {sendEmailForm.send_email ? (
              <Button 
                size="sm" 
                onClick={handleSendWithEmail} 
                disabled={sendingWithEmail || !sendEmailForm.email_to}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="send-with-email-btn"
              >
                {sendingWithEmail ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
                Send & Email
              </Button>
            ) : (
              <Button 
                size="sm" 
                onClick={handleSendOnly} 
                disabled={sendingWithEmail}
                data-testid="send-only-btn"
              >
                {sendingWithEmail ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                Send Only
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Qty Discrepancy Dialog */}
      <QtyDiscrepancyDialog
        open={discrepancyOpen}
        onClose={() => { setDiscrepancyOpen(false); setDiscrepancyChallan(null); loadChallans(); }}
        sourceType="challan"
        sourceId={discrepancyChallan?.id || ''}
        sourceNumber={discrepancyChallan?.challan_number || ''}
        partyType={discrepancyChallan?.order_type === 'inbound' ? 'customer' : 'supplier'}
        partyName={discrepancyChallan?.customer_name || discrepancyChallan?.supplier_name || ''}
        partyId={discrepancyChallan?.customer_id || discrepancyChallan?.supplier_id || ''}
        partyEmail={discrepancyChallan?.customer_email || discrepancyChallan?.supplier_email || ''}
        invoiceNumber={discrepancyChallan?.customer_challan_number || ''}
        godowns={godowns}
        items={(discrepancyChallan?.items || []).map((ci, idx) => {
          // Resolve the actual godown where items were received into.
          // Inbound challans (customer-supplied RM) sit in the line's
          // staging_godown_id (INBOUND_STAGING) until QC moves them out.
          // Outbound challans get received back into STORE.
          const isInbound = discrepancyChallan?.order_type === 'inbound';
          const storeGd = godowns.find(g => g.code === 'STORE');
          const stagingGd = godowns.find(g => g.code === 'INBOUND_STAGING');
          const itemGodownId = isInbound
            ? (ci.staging_godown_id || stagingGd?.id || '')
            : (storeGd?.id || '');
          return {
            item_id: ci.item_id,
            item_name: ci.item_name,
            item_unique_id: ci.item_unique_id || '',
            expected_qty: ci.quantity || 0,
            received_qty: ci.received_quantity || 0,
            unit_of_measure: ci.unit_of_measure || 'Nos',
            godown_id: itemGodownId,
            challan_line_index: idx,
          };
        })}
      />

      {/* Force Close Remarks Dialog */}
      <Dialog open={!!forceClosePrompt} onOpenChange={(open) => { if (!open) { setForceClosePrompt(null); setForceCloseRemarks(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Force Close: {forceClosePrompt?.challan?.challan_number}
            </DialogTitle>
            <DialogDescription>This will close the challan regardless of pending items. Please record your reason.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Remarks / Reason for Force Close *</Label>
              <Textarea
                value={forceCloseRemarks}
                onChange={(e) => setForceCloseRemarks(e.target.value)}
                placeholder="Enter reason for force closing this challan..."
                className="mt-1 text-sm"
                rows={3}
                data-testid="force-close-remarks"
              />
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => { setForceClosePrompt(null); setForceCloseRemarks(''); }}>Cancel</Button>
            <Button
              disabled={!forceCloseRemarks.trim() || closingChallan}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirm-force-close-btn"
              onClick={() => {
                if (forceClosePrompt?.type === 'outbound') {
                  handleCloseChallan(forceClosePrompt.challan, true, forceCloseRemarks.trim());
                } else {
                  handleCloseInbound(true, forceCloseRemarks.trim());
                }
              }}
            >
              {closingChallan ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Closing...</> : <><AlertTriangle className="w-3.5 h-3.5 mr-1" /> Confirm Force Close</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen Challan Remarks Dialog */}
      <Dialog open={!!reopenPrompt} onOpenChange={(open) => { if (!open) { setReopenPrompt(null); setReopenRemarks(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <RotateCcw className="w-4 h-4 text-blue-600" />
              Reopen: {reopenPrompt?.challan?.challan_number}
            </DialogTitle>
            <DialogDescription>This will revert the challan to its previous status. Please record your reason.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Reason for Reopening *</Label>
              <Textarea
                value={reopenRemarks}
                onChange={(e) => setReopenRemarks(e.target.value)}
                placeholder="Enter reason for reopening this challan..."
                className="mt-1 text-sm"
                rows={3}
                data-testid="reopen-remarks"
              />
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => { setReopenPrompt(null); setReopenRemarks(''); }}>Cancel</Button>
            <Button
              disabled={!reopenRemarks.trim() || reopening}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="confirm-reopen-btn"
              onClick={handleReopenChallan}
            >
              {reopening ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Reopening...</> : <><RotateCcw className="w-3.5 h-3.5 mr-1" /> Confirm Reopen</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
